/**
 * RB0840S Cascading Variant Selector with Visual Enhancement
 * 
 * Features:
 * - Visual thumbnail gallery for quick preview
 * - Cascading dropdowns (Frame → Lens → Size)
 * - Automatic variant selection
 * - Proper Add to Cart integration
 */

// COMPLETE CONSOLE SILENCE - NO OUTPUT WHATSOEVER
(function() {
  // Override ALL console methods immediately
  const emptyFunction = function() {};
  
  console.log = emptyFunction;
  console.warn = emptyFunction;
  console.error = emptyFunction;
  console.info = emptyFunction;
  console.debug = emptyFunction;
  console.trace = emptyFunction;
  console.table = emptyFunction;
  console.group = emptyFunction;
  console.groupEnd = emptyFunction;
  console.groupCollapsed = emptyFunction;
  console.time = emptyFunction;
  console.timeEnd = emptyFunction;
  console.count = emptyFunction;
  console.assert = emptyFunction;
  console.clear = emptyFunction;
  console.dir = emptyFunction;
  console.dirxml = emptyFunction;
  
  // Block any future console assignments
  Object.defineProperty(window, 'console', {
    value: {
      log: emptyFunction,
      warn: emptyFunction,
      error: emptyFunction,
      info: emptyFunction,
      debug: emptyFunction,
      trace: emptyFunction,
      table: emptyFunction,
      group: emptyFunction,
      groupEnd: emptyFunction,
      groupCollapsed: emptyFunction,
      time: emptyFunction,
      timeEnd: emptyFunction,
      count: emptyFunction,
      assert: emptyFunction,
      clear: emptyFunction,
      dir: emptyFunction,
      dirxml: emptyFunction
    },
    writable: false,
    configurable: false
  });
})();

(function() {
  'use strict';
  
  const CascadingVisualSelector = {
    
    init: function() {
      // Get product data from Shopify's global object
      if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        this.product = ShopifyAnalytics.meta.product;
        this.variants = this.product.variants;
      } else if (typeof window.product !== 'undefined') {
        this.product = window.product;
        this.variants = this.product.variants || [];
      } else {
        setTimeout(() => this.init(), 500);
        return;
      }
      
      // Get variants from variant selector if not in product object
      if (!this.variants || this.variants.length === 0) {
        this.variants = this.getVariantsFromDOM();
      }
      
      // Find variant selectors
      this.frameSelect = document.querySelector('select[data-option="option1"], select[name*="option1"], .variant-picker select:first-of-type');
      this.lensSelect = document.querySelector('select[data-option="option2"], select[name*="option2"], .variant-picker select:nth-of-type(2)');
      this.sizeSelect = document.querySelector('select[data-option="option3"], select[name*="option3"], .variant-picker select:nth-of-type(3)');
      
      if (!this.frameSelect || !this.lensSelect) {
        return;
      }
      
      this.setupVisualGallery();
      this.setupCascading();
      this.setupVariantSelection();
    },
    
    getVariantsFromDOM: function() {
      // Extract variants from the main variant selector
      const variantSelect = document.querySelector('select[name="id"]');
      if (!variantSelect) return [];
      
      const variants = [];
      Array.from(variantSelect.options).forEach(option => {
        if (!option.value) return;
        
        // Parse option text like "Black / Grey / M - $151.00"
        const text = option.textContent.trim();
        const parts = text.split(' / ');
        
        if (parts.length >= 2) {
          const variant = {
            id: option.value,
            option1: parts[0]?.trim(),
            option2: parts[1]?.trim(),
            option3: parts[2]?.split(' - ')[0]?.trim(),
            title: text,
            available: !option.disabled
          };
          
          // Try to get image from data attribute or find matching image
          const imageUrl = option.dataset.image || this.findVariantImage(variant);
          if (imageUrl) {
            variant.featured_image = { src: imageUrl };
          }
          
          variants.push(variant);
        }
      });
      
      return variants;
    },
    
    findVariantImage: function(variant) {
      // Try to find image for this variant from product images
      const productImages = document.querySelectorAll('.product__media img, [data-product-media] img');
      // For now, return first image as fallback
      return productImages[0]?.src || '';
    },
    
    setupVisualGallery: function() {
      // Create visual gallery container
      const variantPicker = document.querySelector('.variant-picker, .product-form__input');
      if (!variantPicker) return;
      
      const gallery = document.createElement('div');
      gallery.className = 'visual-gallery';
      gallery.innerHTML = '<h4>Quick Preview</h4><div class="visual-gallery-grid" id="visual-gallery-grid"></div>';
      
      // Insert before variant picker
      variantPicker.parentNode.insertBefore(gallery, variantPicker);
      
      this.renderThumbnails();
    },
    
    renderThumbnails: function() {
      const grid = document.getElementById('visual-gallery-grid');
      if (!grid) return;
      
      // Get unique frame+lens combinations with images
      const combinations = {};
      
      this.variants.forEach(variant => {
        const frame = variant.option1;
        const lens = variant.option2;
        const key = `${frame}-${lens}`;
        
        if (!combinations[key] && variant.featured_image) {
          combinations[key] = {
            frame: frame,
            lens: lens,
            image: variant.featured_image.src,
            variant: variant
          };
        }
      });
      
      // Render thumbnails (show first 3, rest in +N button)
      const combosArray = Object.values(combinations);
      const maxVisible = 3;
      
      combosArray.forEach((combo, index) => {
        if (index < maxVisible) {
          const thumb = this.createThumbnail(combo);
          grid.appendChild(thumb);
        }
      });
      
      // Add "show more" button if needed
      if (combosArray.length > maxVisible) {
        const moreBtn = document.createElement('div');
        moreBtn.className = 'visual-thumb more-btn';
        moreBtn.innerHTML = `<span>+${combosArray.length - maxVisible}</span>`;
        moreBtn.onclick = () => this.expandGallery(combosArray, maxVisible);
        grid.appendChild(moreBtn);
      }
    },
    
    createThumbnail: function(combo) {
      const thumb = document.createElement('div');
      thumb.className = 'visual-thumb';
      thumb.innerHTML = `<img src="${combo.image}" alt="${combo.frame} - ${combo.lens}">`;
      
      thumb.onclick = () => {
        // Update selectors
        this.frameSelect.value = combo.frame;
        this.frameSelect.dispatchEvent(new Event('change', { bubbles: true }));
        
        setTimeout(() => {
          this.lensSelect.value = combo.lens;
          this.lensSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }, 100);
        
        // Highlight selected
        document.querySelectorAll('.visual-thumb').forEach(t => t.classList.remove('selected'));
        thumb.classList.add('selected');
      };
      
      return thumb;
    },
    
    expandGallery: function(combosArray, startIndex) {
      const grid = document.getElementById('visual-gallery-grid');
      grid.classList.add('expanded');
      
      // Remove more button
      const moreBtn = grid.querySelector('.more-btn');
      if (moreBtn) moreBtn.remove();
      
      // Add remaining thumbnails
      for (let i = startIndex; i < combosArray.length; i++) {
        const thumb = this.createThumbnail(combosArray[i]);
        grid.appendChild(thumb);
      }
    },
    
    setupCascading: function() {
      const self = this;
      
      // When frame changes, update available lens options
      this.frameSelect.addEventListener('change', function() {
        self.updateLensOptions(this.value);
      });
      
      // When lens changes, update available sizes
      this.lensSelect.addEventListener('change', function() {
        self.updateSizeOptions(self.frameSelect.value, this.value);
      });
    },
    
    updateLensOptions: function(selectedFrame) {
      // Get available lens colors for selected frame from ACTUAL EXISTING VARIANTS
      const availableLens = new Set();
      
      // STRATEGY 1: Use variantDataMap (most accurate)
      if (window.variantDataMap) {
        Object.keys(window.variantDataMap).forEach(key => {
          const frameLensOnly = key.split(' / ')[0]; // Remove size part
          
          // Check if this variant starts with the selected frame
          if (frameLensOnly.startsWith(selectedFrame + ' - ')) {
            const lensColor = frameLensOnly.substring((selectedFrame + ' - ').length);
            if (lensColor) {
              availableLens.add(lensColor);
            }
          }
        });
      }
      
      // STRATEGY 2: Use ShopifyAnalytics as fallback
      if (availableLens.size === 0) {
        this.variants.forEach(variant => {
          if (variant.option1 && variant.option1.startsWith(selectedFrame + ' - ')) {
            const lensColor = variant.option1.substring((selectedFrame + ' - ').length);
            if (lensColor) {
              availableLens.add(lensColor);
            }
          }
        });
      }
      
      // Update lens dropdown - DISABLE options that don't exist
      Array.from(this.lensSelect.options).forEach(option => {
        if (option.value === '') return; // Keep placeholder
        
        // Check if this Frame + Lens combination exists
        const frameLensCombination = selectedFrame + ' - ' + option.value;
        const existingVariants = this.getExistingVariantOptions();
        
        option.disabled = !existingVariants.includes(frameLensCombination);
      });
      
      // Select first available lens
      const firstAvailable = Array.from(availableLens)[0];
      if (firstAvailable) {
        this.lensSelect.value = firstAvailable;
        this.lensSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
    
    updateSizeOptions: function(selectedFrame, selectedLens) {
      // Get available sizes for selected frame+lens from ACTUAL EXISTING VARIANTS
      const availableSizes = new Set();
      const frameLensCombination = selectedFrame + ' - ' + selectedLens;
      
      // STRATEGY 1: Use variantDataMap (most accurate)
      if (window.variantDataMap) {
        Object.keys(window.variantDataMap).forEach(key => {
          // Keys are like "Black - Grey / M"
          if (key.startsWith(frameLensCombination + ' / ')) {
            const size = key.split(' / ')[1];
            if (size) {
              availableSizes.add(size);
            }
          }
        });
      }
      
      // STRATEGY 2: Use ShopifyAnalytics as fallback
      if (availableSizes.size === 0) {
        this.variants.forEach(variant => {
          if (variant.option1 === frameLensCombination) {
            if (variant.option2) availableSizes.add(variant.option2);
            if (variant.option3) availableSizes.add(variant.option3);
          }
        });
      }
      
      // Update size dropdown
      if (this.sizeSelect) {
        Array.from(this.sizeSelect.options).forEach(option => {
          if (option.value === '') return;
          option.disabled = !availableSizes.has(option.value);
        });
        
        // Select first available size
        const firstAvailable = Array.from(availableSizes)[0];
        if (firstAvailable) {
          this.sizeSelect.value = firstAvailable;
          this.sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    },
    
    setupVariantSelection: function() {
      // Listen to all option changes and update main product
      const self = this;
      
      [this.frameSelect, this.lensSelect, this.sizeSelect].forEach(select => {
        if (!select) return;
        
        select.addEventListener('change', function() {
          self.updateSelectedVariant();
        });
      });
    },
    
    updateSelectedVariant: function() {
      const selectedFrame = this.frameSelect.value;
      const selectedLens = this.lensSelect.value;
      const selectedSize = this.sizeSelect ? this.sizeSelect.value : '';
      
      // Find matching variant
      const matchingVariant = this.variants.find(v => 
        v.option1 === selectedFrame && 
        v.option2 === selectedLens && 
        (!selectedSize || v.option3 === selectedSize)
      );
      
      if (matchingVariant) {
        // Update main variant selector
        const variantSelect = document.querySelector('select[name="id"]');
        if (variantSelect) {
          variantSelect.value = matchingVariant.id;
          variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        // Update main image
        if (matchingVariant.featured_image) {
          this.updateMainImage(matchingVariant.featured_image.src);
        }
        
        // Update price
        this.updatePrice(matchingVariant);
      }
    },
    
    updateMainImage: function(imageSrc) {
      const mainImage = document.querySelector('.product__media img, .product-media img, [data-product-media] img');
      if (mainImage) {
        mainImage.src = imageSrc;
        mainImage.srcset = imageSrc;
      }
    },
    
    updatePrice: function(variant) {
      const priceElement = document.querySelector('.price__regular .price-item--regular');
      if (priceElement && variant.price) {
        priceElement.textContent = Shopify.formatMoney(variant.price, window.theme.moneyFormat || '${{amount}}');
      }
    }
  };
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CascadingVisualSelector.init());
  } else {
    CascadingVisualSelector.init();
  }
  
  window.cascadingVisualSelector = CascadingVisualSelector;
  
})();


/**
 * ========================================
 * FRAME-LENS VISUAL THUMBNAILS
 * ========================================
 * Converts Frame-Lens dropdown to visual thumbnail selector
 */

(function() {
  'use strict';

  const FrameLensThumbnails = {
    
    currentVariantId: null, // Track current selected variant
    currentVariantImages: [], // Track current variant's images for arrow navigation
    currentImageIndex: 0, // Track current image index for arrows
    customPrevArrow: null, // Reference to our custom previous arrow
    customNextArrow: null, // Reference to our custom next arrow
    
    init: function() {
      // MODERN THEME: Find variant pickers (radio buttons, not selects)
      const variantPickers = document.querySelectorAll(
        '.variant-picker, .product-form__input, [data-variant-picker], .product-variant-picker'
      );
      
      if (variantPickers.length === 0) {
        setTimeout(() => this.init(), 500);
        return;
      }

      // Find Frame-Lens picker specifically (not Size!)
      let frameLensPicker = null;
      variantPickers.forEach(picker => {
        const label = picker.querySelector('legend, label, .product-form__label');
        const labelText = label ? label.textContent.toLowerCase() : '';
        
        // Only convert Frame-Lens, NOT Size
        if (labelText.includes('frame') || labelText.includes('lens')) {
          if (!labelText.includes('size')) {  // Make sure it's not Size option
            frameLensPicker = picker;
            // console.log('Found Frame-Lens picker:', label.textContent);
          }
        }
      });

      if (!frameLensPicker) {
        // console.log('Frame-Lens picker not found');
        // DO NOT use first picker as fallback - we only want Frame-Lens!
        return;
      }

      if (frameLensPicker) {
        this.createVisualSelector(frameLensPicker);
        
        // Hide ALL Size labels/legends - more aggressive approach
        setTimeout(() => {
          // Find all variant pickers
          const allPickers = document.querySelectorAll('.variant-picker, .product-form__input, fieldset, .product-form__controls');
          allPickers.forEach(picker => {
            // Hide legend
            const legend = picker.querySelector('legend');
            if (legend && legend.textContent.toLowerCase().includes('size')) {
              legend.style.display = 'none';
              // console.log('✓ Size legend hidden');
            }
            
            // Hide label
            const label = picker.querySelector('.product-form__label, label');
            if (label && label.textContent.toLowerCase().includes('size')) {
              label.style.display = 'none';
              // console.log('✓ Size label hidden');
            }
            
            // Hide any heading
            const heading = picker.querySelector('h3, h4, .variant-label');
            if (heading && heading.textContent.toLowerCase().includes('size')) {
              heading.style.display = 'none';
              // console.log('✓ Size heading hidden');
            }
          });
          
          // Also check for standalone Size text
          document.querySelectorAll('legend, label, .product-form__label, h3, h4').forEach(el => {
            if (el.textContent.trim().toLowerCase() === 'size') {
              el.style.display = 'none';
              // console.log('✓ Standalone Size text hidden');
            }
          });
          
          // Hide original Lens Type / Polarized options from theme
          document.querySelectorAll('.variant-picker, .product-form__input, fieldset').forEach(picker => {
            const label = picker.querySelector('legend, .product-form__label, label');
            const labelText = label ? label.textContent.toLowerCase() : '';
            
            // Hide Lens Type, Polarized, or similar options
            if (labelText.includes('lens type') || 
                labelText.includes('lense type') ||
                labelText.includes('polarized') ||
                labelText.includes('polarised')) {
              picker.style.display = 'none';
              // console.log('✓ Original Lens Type option hidden');
            }
          });
        }, 200);
        
        // Listen for Size changes to update display
        setTimeout(() => {
          const sizeInputs = document.querySelectorAll('input[type="radio"][name*="Size"], input[type="radio"][name*="size"]');
          sizeInputs.forEach(input => {
            input.addEventListener('change', function() {
              // Trigger display update
              const activeThumb = document.querySelector('.frame-lens-option.active .frame-lens-label');
              if (activeThumb) {
                FrameLensThumbnails.showSelectedText(activeThumb.textContent, frameLensPicker);
              }
            });
          });
          
          const sizeSelect = document.querySelector('select[name*="Size"], select[name*="size"]');
          if (sizeSelect) {
            sizeSelect.addEventListener('change', function() {
              const activeThumb = document.querySelector('.frame-lens-option.active .frame-lens-label');
              if (activeThumb) {
                FrameLensThumbnails.showSelectedText(activeThumb.textContent, frameLensPicker);
              }
            });
          }
        }, 500);
      }
      
      // Intercept Add to Cart form submission to ensure correct variant
      this.setupAddToCartInterceptor();
      
      // CRITICAL: Protect thumbnails from being hidden by theme's variant change handlers
      this.protectThumbnailsFromHiding();
    },
    
    protectThumbnailsFromHiding: function() {
      // console.log('=== Setting up thumbnail protection ===');
      
      // Monitor the thumbnail container and prevent it from being hidden
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            const target = mutation.target;
            
            // If thumbnail container is being hidden, prevent it
            if (target.classList.contains('frame-lens-visual-selector')) {
              if (target.style.display === 'none' || target.style.visibility === 'hidden') {
                // console.log('⚠️ Thumbnail hiding detected - preventing!');
                target.style.display = '';
                target.style.visibility = '';
              }
            }
            
            // Also protect individual thumbnails
            if (target.classList.contains('frame-lens-option')) {
              if (target.style.display === 'none' || target.style.visibility === 'hidden') {
                // console.log('⚠️ Individual thumbnail hiding detected - preventing!');
                target.style.display = '';
                target.style.visibility = '';
              }
            }
          }
          
          // If thumbnails are being removed from DOM, prevent it
          if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
            mutation.removedNodes.forEach(node => {
              if (node.classList && (node.classList.contains('frame-lens-visual-selector') || 
                                     node.classList.contains('frame-lens-option'))) {
                // console.log('⚠️ Thumbnail removal detected - preventing!');
                mutation.target.appendChild(node);
              }
            });
          }
        });
      });
      
      // Observe the entire product form area
      const productForm = document.querySelector('.product-form, [data-product-form], .product__info-wrapper');
      if (productForm) {
        observer.observe(productForm, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['style', 'class']
        });
        // console.log('✓ Thumbnail protection active');
      }
      
      // Also add a periodic check to ensure thumbnails stay visible
      setInterval(() => {
        const thumbnailContainer = document.querySelector('.frame-lens-visual-selector');
        if (thumbnailContainer) {
          if (thumbnailContainer.style.display === 'none' || 
              thumbnailContainer.style.visibility === 'hidden' ||
              window.getComputedStyle(thumbnailContainer).display === 'none') {
            // console.log('⚠️ Periodic check: Thumbnails hidden - restoring!');
            thumbnailContainer.style.display = '';
            thumbnailContainer.style.visibility = 'visible';
          }
        }
      }, 500); // Check every 500ms
    },

    setupAddToCartInterceptor: function() {
      // console.log('Setting up Add to Cart interceptor...');
      
      // Find product form
      const productForm = document.querySelector('form[action*="/cart/add"], form.product-form, [data-product-form]');
      
      if (productForm) {
        productForm.addEventListener('submit', function(e) {
          // Get the variant select
          const variantSelect = document.querySelector('select[name="id"]');
          
          if (variantSelect && variantSelect.value) {
            // console.log('=== ADD TO CART ===');
            // console.log('Variant ID being added:', variantSelect.value);
            
            // Find variant details
            if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
              const product = ShopifyAnalytics.meta.product;
              const variant = product.variants.find(v => v.id == variantSelect.value);
              
              if (variant) {
                // console.log('Variant Name:', variant.public_title || variant.name);
                // console.log('Variant Price:', `$${(variant.price / 100).toFixed(2)}`);
                // console.log('✓ Correct variant will be added to cart');
              }
            }
          } else {
            console.error('✗ No variant selected!');
          }
        });
        
        // console.log('✓ Add to Cart interceptor ready');
      }
      
      // Also intercept Buy Now button if exists
      const buyNowBtn = document.querySelector('[name="checkout"], .shopify-payment-button__button');
      if (buyNowBtn) {
        buyNowBtn.addEventListener('click', function(e) {
          const variantSelect = document.querySelector('select[name="id"]');
          if (variantSelect && variantSelect.value) {
            // console.log('=== BUY NOW ===');
            // console.log('Variant ID:', variantSelect.value);
          }
        });
      }
    },

    createVisualSelector: function(picker) {
      if (picker.dataset.visualized) return;
      picker.dataset.visualized = 'true';

      // console.log('Creating visual selector...');

      // Get all radio buttons or options
      const inputs = picker.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      
      if (inputs.length === 0) {
        // console.log('No radio inputs found');
        return;
      }

      // console.log('Found inputs:', inputs.length);

      // Hide original inputs container
      const originalContainer = picker.querySelector('.variant-picker__options, .product-form__input, fieldset > div');
      if (originalContainer && !originalContainer.classList.contains('frame-lens-visual-selector')) {
        originalContainer.style.display = 'none';
        // console.log('✓ Original text buttons hidden');
      }

      // Also hide all labels that are not part of thumbnails
      const allLabels = picker.querySelectorAll('label');
      allLabels.forEach(label => {
        if (!label.classList.contains('frame-lens-label')) {
          label.style.display = 'none';
        }
      });

      // Create visual container
      const container = document.createElement('div');
      container.className = 'frame-lens-visual-selector';
      
      // Get variant images
      const variantImages = this.getVariantImages();
      const galleryImages = this.getGalleryImages();
      
      // console.log('Variant images:', Object.keys(variantImages).length);
      // console.log('Gallery images:', galleryImages.length);

      // Store all thumbnails data
      const allThumbnails = [];
      const maxVisible = 3; // Show first 3 thumbnails
      
      // CRITICAL FIX: Get list of actual existing variants to filter against
      const existingVariants = this.getExistingVariantOptions();
      
      // Create thumbnail for each option
      inputs.forEach((input, index) => {
        const label = picker.querySelector(`label[for="${input.id}"]`);
        const optionText = label ? label.textContent.trim() : input.value;
        
        // SKIP Size options - only process Frame-Lens combinations
        if (optionText === 'M' || optionText === 'L' || optionText === 'S' || 
            optionText === 'XL' || optionText === 'XXL' || optionText === 'XXS' ||
            optionText.length <= 2) {  // Single letter/number = likely size
          // console.log('Skipping size option:', optionText);
          return; // Skip this input
        }
        
        // CRITICAL FIX: Only show thumbnails for variants that actually exist
        if (!existingVariants.includes(optionText)) {
          // console.log('Skipping non-existent variant:', optionText);
          return; // Skip this variant - it doesn't exist in the product
        }
        
        // Get image - try variant images first, then gallery images
        let imageUrl = variantImages[input.value] || 
                      variantImages[optionText] ||
                      variantImages[optionText.toLowerCase()] ||
                      this.findImageForVariant(optionText) ||
                      galleryImages[index]; // Use gallery image by index
        
        // console.log(`"${optionText}":`, imageUrl ? '✓' : '✗');
        
        allThumbnails.push({
          input: input,
          optionText: optionText,
          imageUrl: imageUrl,
          index: index
        });
      });
      
      // Render first 3 thumbnails
      allThumbnails.slice(0, maxVisible).forEach((thumbData) => {
        const button = this.createThumbnailButton(thumbData, container, picker);
        container.appendChild(button);
      });
      
      // Add "Show More" button if there are more than 3 thumbnails
      if (allThumbnails.length > maxVisible) {
        const showMoreBtn = document.createElement('button');
        showMoreBtn.type = 'button';
        showMoreBtn.className = 'frame-lens-option show-more';
        
        const countSpan = document.createElement('span');
        countSpan.className = 'show-more-count';
        countSpan.textContent = `+${allThumbnails.length - maxVisible}`;
        showMoreBtn.appendChild(countSpan);
        
        showMoreBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          
          const totalThumbnails = allThumbnails.length;
          // console.log('Show more clicked - Total thumbnails:', totalThumbnails);
          
          // Remove show more button
          this.remove();
          
          // Add remaining thumbnails
          allThumbnails.slice(maxVisible).forEach((thumbData) => {
            const button = FrameLensThumbnails.createThumbnailButton(thumbData, container, picker);
            button.classList.add('hidden-thumb'); // Mark as initially hidden
            container.appendChild(button);
          });
          
          // Expand container to GRID layout
          container.classList.add('expanded');
          container.classList.add('grid-layout');
          
          // Add vertical scroll ONLY if more than 9 thumbnails (3 rows)
          // 6 thumbnails = 2 rows, no scroll needed
          // 12+ thumbnails = 4+ rows, scroll needed
          if (totalThumbnails > 9) {
            container.style.maxHeight = '400px'; // Show ~3 rows
            container.style.overflowY = 'auto';
            container.style.overflowX = 'hidden';
            // console.log('✓ Grid with vertical scroll (', totalThumbnails, 'thumbnails)');
          } else {
            // No scroll needed for small number of thumbnails
            container.style.maxHeight = 'none';
            container.style.overflowY = 'visible';
            // console.log('✓ Grid without scroll (', totalThumbnails, 'thumbnails - fits in view)');
          }
        });
        
        container.appendChild(showMoreBtn);
      }
      
      picker.appendChild(container);
      // console.log('✓ Visual selector created');
      
      // Auto-select first thumbnail
      setTimeout(() => {
        const firstButton = container.querySelector('.frame-lens-option:first-child');
        if (firstButton) {
          // console.log('Auto-selecting first thumbnail');
          firstButton.click();
        }
      }, 100);
    },

    createThumbnailButton: function(thumbData, container, picker) {
      const { input, optionText, imageUrl, index } = thumbData;
      
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'frame-lens-option';
      button.dataset.value = input.value;
      button.dataset.inputId = input.id;
      
      if (input.checked || index === 0) {
        button.classList.add('active');
      }
      
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = optionText;
        img.loading = 'lazy';
        
        // Prevent gallery lightbox from opening
        img.style.pointerEvents = 'none';
        
        img.onerror = function() {
          console.error('Failed:', imageUrl);
          this.style.display = 'none';
        };
        button.appendChild(img);
      } else {
        const placeholder = document.createElement('div');
        placeholder.style.cssText = 'width:80px;height:80px;background:#f0f0f0;display:flex;align-items:center;justify-content:center;border-radius:4px;';
        placeholder.textContent = '?';
        button.appendChild(placeholder);
      }
      
      const labelSpan = document.createElement('span');
      labelSpan.className = 'frame-lens-label';
      labelSpan.textContent = optionText;
      labelSpan.style.display = 'none'; // Hide text label
      button.appendChild(labelSpan);
      
      button.addEventListener('click', function(e) {
        // GENTLE: Only prevent default, don't block other events for mini cart to work
        e.preventDefault();
        // REMOVED: e.stopPropagation() and e.stopImmediatePropagation() to allow mini cart functionality
        
        const clickedImageUrl = imageUrl;
        
        // console.log('Thumbnail clicked:', optionText);
        
        // Temporarily disable gallery/lightbox clicks
        const galleryElements = document.querySelectorAll(
          '.product__media, .product-media, [data-product-media], slideshow-container, .product__modal-opener'
        );
        galleryElements.forEach(el => {
          el.style.pointerEvents = 'none';
        });
        
        // 1. Update active state
        container.querySelectorAll('.frame-lens-option').forEach(btn => btn.classList.remove('active'));
        this.classList.add('active');
        
        // 2. Trigger original radio button
        const originalInput = document.getElementById(button.dataset.inputId);
        if (originalInput) {
          originalInput.checked = true;
          originalInput.dispatchEvent(new Event('change', { bubbles: true }));
          originalInput.dispatchEvent(new Event('click', { bubbles: true }));
        }
        
        // 3. Show selected text in Frame-Lens label
        FrameLensThumbnails.showSelectedText(optionText, picker);
        
        // 4. IMMEDIATELY select matching variant (before image update)
        FrameLensThumbnails.selectMatchingVariantWithSize(optionText);
        
        // 5. Update main product image (with delay to prevent zoom)
        setTimeout(() => {
          if (clickedImageUrl) {
            FrameLensThumbnails.updateMainProductImage(clickedImageUrl, optionText);
          }
          
          // Re-enable gallery after image change
          setTimeout(() => {
            galleryElements.forEach(el => {
              el.style.pointerEvents = '';
            });
          }, 300);
        }, 50);
        
        return false;
      }, true); // Use capture phase
      
      return button;
    },

    getExistingVariantOptions: function() {
      const existingOptions = [];
      
      // STRATEGY 1: Use variantDataMap from Liquid template (most accurate)
      if (window.variantDataMap) {
        Object.keys(window.variantDataMap).forEach(key => {
          // Keys can be "Black - Grey" or "Black - Grey / M"
          // We want just the Frame-Lens part (before the " / ")
          const frameLensOnly = key.split(' / ')[0];
          if (!existingOptions.includes(frameLensOnly)) {
            existingOptions.push(frameLensOnly);
          }
        });
        
        if (existingOptions.length > 0) {
          // console.log('✓ Found existing variants from variantDataMap:', existingOptions);
          return existingOptions;
        }
      }
      
      // STRATEGY 2: Use ShopifyAnalytics product variants
      if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        const product = ShopifyAnalytics.meta.product;
        
        if (product.variants) {
          product.variants.forEach(variant => {
            const option1 = variant.option1; // This should be Frame-Lens combination
            if (option1 && !existingOptions.includes(option1)) {
              existingOptions.push(option1);
            }
          });
          
          if (existingOptions.length > 0) {
            // console.log('✓ Found existing variants from ShopifyAnalytics:', existingOptions);
            return existingOptions;
          }
        }
      }
      
      // STRATEGY 3: Parse from main variant selector dropdown
      const variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect) {
        Array.from(variantSelect.options).forEach(option => {
          if (!option.value || option.disabled) return;
          
          // Parse option text like "Black - Grey / M - $151.00"
          const text = option.textContent.trim();
          const parts = text.split(' / ');
          
          if (parts.length >= 1) {
            // Get Frame-Lens part (before size and price)
            let frameLens = parts[0].trim();
            
            // Remove price if it exists (anything after " - $")
            if (frameLens.includes(' - $')) {
              frameLens = frameLens.split(' - $')[0].trim();
            }
            
            if (frameLens && !existingOptions.includes(frameLens)) {
              existingOptions.push(frameLens);
            }
          }
        });
      }
      
      // console.log('✓ Found existing variants from variant selector:', existingOptions);
      return existingOptions;
    },

    getVariantImages: function() {
      const images = {};
      
      // console.log('=== Fetching Variant Images ===');
      
      // STRATEGY 1: Check for hidden variant image divs (from Liquid template)
      const variantImageDivs = document.querySelectorAll('.custom-variant-image[data-variant-option1]');
      
      if (variantImageDivs.length > 0) {
        // console.log('✓ Found', variantImageDivs.length, 'variant images from Liquid template');
        
        // Initialize variant data map
        if (!window.variantDataMap) {
          window.variantDataMap = {};
        }
        
        variantImageDivs.forEach((div, index) => {
          const option1 = div.dataset.variantOption1; // Frame-Lens
          const option2 = div.dataset.variantOption2; // Size
          const imageSrc = div.dataset.imageSrc || div.querySelector('img')?.src;
          const variantId = div.dataset.variantId;
          const variantPrice = div.dataset.variantPrice;
          const variantTitle = div.dataset.variantTitle;
          
          if (option1 && imageSrc) {
            // Store image with option1 key
            images[option1] = imageSrc;
            images[option1.toLowerCase()] = imageSrc;
            images[variantId] = imageSrc;
            
            // Store complete variant data with BOTH keys:
            // 1. Frame-Lens only: "Black - Grey"
            // 2. Frame-Lens + Size: "Black - Grey / M"
            const variantData = {
              id: variantId,
              price: variantPrice,
              image: imageSrc,
              title: variantTitle,
              option1: option1,
              option2: option2
            };
            
            // Key 1: Just Frame-Lens
            window.variantDataMap[option1] = variantData;
            
            // Key 2: Frame-Lens + Size (full variant title)
            if (option2) {
              const fullKey = `${option1} / ${option2}`;
              window.variantDataMap[fullKey] = variantData;
            }
            
            // console.log(`${index + 1}. "${option1}" (${option2}) → ✓ $${(variantPrice / 100).toFixed(2)}`);
          }
        });
        
        // console.log(`✓ Loaded ${variantImageDivs.length} variants with images + prices`);
        // console.log('Variant data map keys:', Object.keys(window.variantDataMap).slice(0, 5));
        return images;
      }
      
      // STRATEGY 2: Try ShopifyAnalytics
      // console.log('⚠️ No Liquid template data found, trying ShopifyAnalytics...');
      
      let variants = [];
      
      if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        variants = ShopifyAnalytics.meta.product.variants || [];
        // console.log('✓ Found', variants.length, 'variants in ShopifyAnalytics');
      }
      
      // Map each variant's option1 (Frame-Lens) to its featured image
      let assignedCount = 0;
      variants.forEach((variant, index) => {
        const option1 = variant.option1 || variant.public_title || '';
        
        if (option1 && variant.featured_image && variant.featured_image.src) {
          const imageUrl = variant.featured_image.src;
          
          // Store with multiple keys for easy lookup
          images[option1] = imageUrl;
          images[option1.toLowerCase()] = imageUrl;
          images[variant.id] = imageUrl;
          
          assignedCount++;
          // console.log(`${index + 1}. "${option1}" → ✓ ${imageUrl.substring(imageUrl.lastIndexOf('/') + 1, imageUrl.lastIndexOf('/') + 30)}`);
        } else {
          // console.log(`${index + 1}. "${option1}" → ✗ No image assigned in Shopify`);
        }
      });
      
      // console.log(`\n✓ Variants with images: ${assignedCount} / ${variants.length}`);
      
      // STRATEGY 3: Fallback to gallery matching
      if (assignedCount === 0 && Object.keys(images).length === 0) {
        // console.log('\n⚠️ No variant images found, using gallery fallback...');
        return this.getVariantImagesFromGallery();
      }
      
      return images;
    },

    getVariantImagesFromGallery: function() {
      const images = {};
      
      // console.log('=== Fetching Images from Gallery (Fallback) ===');
      
      // Get ALL product images from gallery
      const productImages = document.querySelectorAll('.product__media img, .product-media img, [data-product-media] img');
      // console.log('Total product images found:', productImages.length);
      
      // Get variant options
      const inputs = document.querySelectorAll('.variant-picker input[type="radio"]');
      // console.log('Total variant options:', inputs.length);
      
      // Match each variant to its specific image by alt text or filename
      inputs.forEach((input, index) => {
        const label = document.querySelector(`label[for="${input.id}"]`);
        const optionText = label ? label.textContent.trim() : input.value;
        
        // Skip size options
        if (optionText.length <= 2) return;
        
        // Extract product code from variant name if possible
        // e.g., "Black - Grey" might have code in image filename
        const frameKeyword = optionText.split('-')[0]?.trim().toLowerCase();
        const lensKeyword = optionText.split('-')[1]?.trim().toLowerCase();
        
        // console.log(`\nVariant ${index + 1}: "${optionText}"`);
        // console.log('  Frame:', frameKeyword, 'Lens:', lensKeyword);
        
        // Find best matching image
        let bestMatch = null;
        let bestScore = 0;
        
        productImages.forEach((img) => {
          const altText = (img.alt || '').toLowerCase();
          const srcText = img.src.toLowerCase();
          
          let score = 0;
          
          // Frame must match
          if (frameKeyword && (altText.includes(frameKeyword) || srcText.includes(frameKeyword))) {
            score += 10;
            
            // Lens match gives bonus
            if (lensKeyword && lensKeyword !== '-') {
              if (altText.includes(lensKeyword) || srcText.includes(lensKeyword)) {
                score += 20;
              }
            }
          }
          
          if (score > bestScore) {
            bestScore = score;
            bestMatch = img.src;
          }
        });
        
        if (bestMatch && bestScore > 0) {
          // console.log(`  ✓ Matched (score ${bestScore}):`, bestMatch.substring(bestMatch.lastIndexOf('/') + 1, bestMatch.lastIndexOf('/') + 30));
          images[input.value] = bestMatch;
          images[optionText] = bestMatch;
          images[optionText.toLowerCase()] = bestMatch;
        } else {
          // console.log(`  ✗ No match found`);
          // Use first image as fallback
          if (productImages.length > 0) {
            images[optionText] = productImages[0].src;
          }
        }
      });
      
      // console.log('\n✓ Gallery fallback complete');
      
      return images;
    },

    getGalleryImages: function() {
      const images = [];
      
      // Get all product media images (you have 271!)
      const mediaImages = document.querySelectorAll(
        '.product__media img, ' +
        '.product-media img, ' +
        '[data-product-media] img, ' +
        '.product-single__photo img, ' +
        'slideshow-container img, ' +
        '.slideshow-controls__thumbnail img'
      );
      
      mediaImages.forEach(img => {
        if (img.src && !images.includes(img.src)) {
          images.push(img.src);
        }
      });
      
      // console.log('Gallery images collected:', images.length);
      return images;
    },

    findImageForVariant: function(optionText) {
      // Directly use product gallery images
      const productImages = document.querySelectorAll('.product__media img, .product-media img, [data-product-media] img');
      
      if (productImages.length > 0) {
        // Try to match by alt text first
        for (let img of productImages) {
          if (img.alt && img.alt.toLowerCase().includes(optionText.toLowerCase())) {
            // console.log('✓ Matched by alt:', optionText);
            return img.src;
          }
        }
        
        // Try to match by filename
        const searchTerms = optionText.toLowerCase().split(/[\s-]+/);
        for (let img of productImages) {
          const imgSrc = img.src.toLowerCase();
          if (searchTerms.some(term => term.length > 3 && imgSrc.includes(term))) {
            // console.log('✓ Matched by filename:', optionText);
            return img.src;
          }
        }
        
        // Use first image as fallback
        // console.log('Using first image for:', optionText);
        return productImages[0].src;
      }
      
      return null;
    },

    findBestImageForVariant: function(variantName) {
      // console.log('Searching best image for:', variantName);
      
      // Get all product images
      const productImages = document.querySelectorAll(
        '.product__media img, .product-media img, [data-product-media] img'
      );
      
      // Extract keywords from variant name
      const keywords = variantName.toLowerCase()
        .replace(/\s*-\s*/g, ' ')
        .split(/\s+/)
        .filter(word => word.length > 2 && word !== 'and');
      
      // console.log('Keywords:', keywords);
      
      let bestMatch = null;
      let bestScore = 0;
      
      productImages.forEach((img, index) => {
        const altText = (img.alt || '').toLowerCase();
        const srcText = img.src.toLowerCase();
        const combinedText = altText + ' ' + srcText;
        
        let score = 0;
        keywords.forEach(keyword => {
          if (combinedText.includes(keyword)) {
            score++;
          }
        });
        
        if (score > bestScore) {
          bestScore = score;
          bestMatch = img.src;
          // console.log(`Better match found (score ${score}):`, img.alt || img.src.substring(img.src.lastIndexOf('/') + 1));
        }
      });
      
      if (bestMatch) {
        // console.log('✓ Best image found with score:', bestScore);
      } else {
        // console.log('✗ No matching image found, using first image');
        bestMatch = productImages[0]?.src;
      }
      
      return bestMatch;
    },

    updateMainProductImage: function(imageUrl, variantName) {
      // console.log('=== Updating Main Image ===');
      // console.log('Variant:', variantName);
      // console.log('Image URL:', imageUrl);
      
      // CRITICAL: Reset image index when variant changes
      this.currentImageIndex = 0;
      
      // If no imageUrl provided, try to find one from gallery
      if (!imageUrl || imageUrl === 'undefined') {
        // console.log('No image URL provided, searching gallery...');
        imageUrl = this.findBestImageForVariant(variantName);
      }
      
      if (!imageUrl) {
        console.error('✗ No image found for variant:', variantName);
        return;
      }
      
      // IMPORTANT: Disable all gallery click handlers temporarily
      const galleryClickables = document.querySelectorAll(
        '.product__media, .product-media, [data-product-media], ' +
        '.product__modal-opener, [data-modal-opener], ' +
        'slideshow-container, .slideshow-controls__thumbnail, ' +
        '.product__media-item, .product-media-item'
      );
      
      // Disable pointer events on gallery
      galleryClickables.forEach(el => {
        el.style.pointerEvents = 'none';
        el.dataset.tempDisabled = 'true';
      });
      
      // Update main product image - ONLY change src, don't click anything
      const mainImages = document.querySelectorAll(
        '.product__media img, ' +
        '.product-media img, ' +
        '[data-product-media] img, ' +
        '.product-single__photo img, ' +
        '.product__main-photos img'
      );
      
      if (mainImages.length > 0) {
        // Smooth fade transition
        mainImages[0].style.transition = 'opacity 0.3s ease';
        mainImages[0].style.opacity = '0';
        
        setTimeout(() => {
          mainImages[0].src = imageUrl;
          mainImages[0].srcset = imageUrl;
          mainImages[0].style.opacity = '1';
          
          // console.log('✓ Main image updated successfully');
          
          // Re-enable gallery clicks after image loads
          setTimeout(() => {
            galleryClickables.forEach(el => {
              if (el.dataset.tempDisabled === 'true') {
                el.style.pointerEvents = '';
                delete el.dataset.tempDisabled;
              }
            });
          }, 500);
        }, 150);
      }
      
      // Update price based on selected variant
      this.updatePriceByName(variantName);
      
      // Update gallery thumbnails for this variant
      this.updateGalleryThumbnails(variantName);
      
      // DON'T click gallery thumbnails - this triggers zoom!
      // Just update the variant select for cart functionality
      const variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect) {
        // Find matching variant by name
        if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
          const product = ShopifyAnalytics.meta.product;
          const matchingVariant = product.variants.find(v => {
            const vName = (v.public_title || v.name || '').toLowerCase();
            const searchName = variantName.toLowerCase();
            return vName.includes(searchName) || searchName.includes(vName);
          });
          
          if (matchingVariant) {
            variantSelect.value = matchingVariant.id;
            variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
            // console.log('✓ Variant select updated (for cart)');
          }
        }
      }
    },

    updateGalleryThumbnails: function(variantName) {
      console.log('=== Updating Gallery Thumbnails for:', variantName, '===');
      
      // CRITICAL: Store current variant images for arrow navigation
      this.currentVariantImages = [];
      this.currentImageIndex = 0;
      
      // Hide original Shopify gallery thumbnails
      const originalGalleries = document.querySelectorAll(
        '.product__media-list, ' +
        '.product-thumbnails, ' +
        '.slideshow-controls, ' +
        '[data-thumbnails], ' +
        '.product__media-thumbnails, ' +
        '.thumbnail-list'
      );
      
      originalGalleries.forEach(gallery => {
        gallery.style.display = 'none';
        console.log('✓ Original gallery hidden');
      });
      
      // Get all product media images - EXCLUDE the main image to prevent duplicates
      const mainImage = document.querySelector('.product__media img, .product-media img');
      const mainImageSrc = mainImage ? mainImage.src : null;
      
      const allMediaImages = document.querySelectorAll(
        '.product__media img, ' +
        '.product-media img, ' +
        '[data-product-media] img, ' +
        'slideshow-container img'
      );
      
      console.log('Total media images found:', allMediaImages.length);
      console.log('Main image src:', mainImageSrc);
      
      // IMPROVED VARIANT IMAGE FILTERING
      // Extract keywords from variant name for matching
      const parts = variantName.split(' - ');
      const frameKeyword = parts[0] ? parts[0].trim().toLowerCase() : '';
      const lensKeyword = parts.slice(1).join(' - ').trim().toLowerCase();
      
      console.log('Frame keyword:', frameKeyword);
      console.log('Lens keyword:', lensKeyword);
      console.log('Full variant name:', variantName);
      
      // Get current product code from URL/handle
      const currentProductHandle = window.location.pathname.split('/products/')[1]?.split('?')[0] || '';
      const currentProductCode = currentProductHandle.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      
      // STRATEGY: Use the FIRST image's filename pattern to identify current product images
      const firstImage = allMediaImages[0];
      let productImagePattern = '';
      
      if (firstImage) {
        const firstFilename = firstImage.src.substring(firstImage.src.lastIndexOf('/') + 1);
        // Extract pattern like "06E000356" from "06E000356__1100L1_030A.png"
        const match = firstFilename.match(/^([A-Z0-9]+)__/i) || firstFilename.match(/^([A-Z0-9]+)_/i);
        if (match) {
          productImagePattern = match[1].toUpperCase();
        }
      }
      
      // IMPROVED: Filter images by variant name with better matching
      const matchingImages = [];
      const variantNameLower = variantName.toLowerCase().trim();
      const frameColor = variantName.split(' - ')[0].trim().toLowerCase();
      
      allMediaImages.forEach((img, index) => {
        const altText = (img.alt || '').toLowerCase().trim();
        const srcText = img.src.toLowerCase();
        const filename = img.src.substring(img.src.lastIndexOf('/') + 1).toUpperCase();
        
        // Check if image belongs to current product
        const belongsToCurrentProduct = !productImagePattern || 
                                       filename.includes(productImagePattern) ||
                                       filename.startsWith(productImagePattern);
        
        if (!belongsToCurrentProduct) {
          return; // Skip images from other products
        }
        
        let score = 0;
        let matchType = '';
        
        // PRIORITY 1: Exact full variant name match (best)
        if (altText === variantNameLower || altText.includes(variantNameLower)) {
          score = 100;
          matchType = 'exact-variant-match';
        }
        // PRIORITY 2: Frame color match + lens keyword match
        else if (altText.includes(frameColor)) {
          score = 80;
          // Bonus points if lens keyword also matches
          if (lensKeyword && lensKeyword !== '-' && altText.includes(lensKeyword)) {
            score = 90;
            matchType = 'frame-lens-match';
          } else {
            matchType = 'frame-color-match';
          }
        }
        // PRIORITY 3: Partial keyword matches
        else if (frameKeyword && altText.includes(frameKeyword)) {
          score = 60;
          matchType = 'frame-keyword-match';
        }
        
        // Add to matching images if score > 0
        if (score > 0) {
          matchingImages.push({
            src: img.src,
            alt: img.alt,
            score: score,
            index: index,
            matchType: matchType
          });
        }
      });
      
      // Sort by score (best matches first)
      matchingImages.sort((a, b) => b.score - a.score);
      
      // CRITICAL FIX: Store ONLY variant-specific images for arrow navigation
      this.currentVariantImages = matchingImages.map(img => img.src);
      
      console.log('✅ VARIANT IMAGES FILTERED:', this.currentVariantImages.length, 'images for', variantName);
      console.log('Images:', this.currentVariantImages.map(src => src.substring(src.lastIndexOf('/') + 1)));
      
      // Update or create gallery thumbnails container
      let galleryContainer = document.querySelector('.variant-gallery-thumbnails');
      
      if (!galleryContainer) {
        // Create new gallery container below main image
        galleryContainer = document.createElement('div');
        galleryContainer.className = 'variant-gallery-thumbnails';
        galleryContainer.style.cssText = `
          display: flex;
          gap: 10px;
          margin: 15px 0;
          padding: 15px;
          overflow-x: auto;
          background: #fff;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
        `;
        
        // Find the main product image container
        const mainImageWrapper = document.querySelector(
          '.product__media-wrapper, ' +
          '.product-media-wrapper, ' +
          '.product__column--media, ' +
          '.product-single__media-wrapper, ' +
          '[data-product-media-wrapper]'
        );
        
        if (mainImageWrapper) {
          mainImageWrapper.appendChild(galleryContainer);
          console.log('✓ Gallery added to media wrapper');
        } else {
          // Fallback: Find slideshow container
          const slideshowContainer = document.querySelector(
            'slideshow-container, ' +
            '.product__media-container, ' +
            '.product-media-container, ' +
            '[data-product-slideshow]'
          );
          
          if (slideshowContainer) {
            slideshowContainer.parentNode.insertBefore(galleryContainer, slideshowContainer.nextSibling);
            console.log('✓ Gallery added after slideshow');
          }
        }
      }
      
      // Clear existing thumbnails
      galleryContainer.innerHTML = '';
      
      // FALLBACK: If no matching images found, use first image only
      if (matchingImages.length === 0) {
        if (allMediaImages.length > 0) {
          matchingImages.push({
            src: allMediaImages[0].src,
            alt: allMediaImages[0].alt,
            score: 0,
            index: 0,
            matchType: 'fallback'
          });
          this.currentVariantImages = [allMediaImages[0].src];
          console.log('⚠️ No variant matches found, using first image as fallback');
        }
      }
      
      // If still no images, show message
      if (matchingImages.length === 0) {
        galleryContainer.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">No images available for this variant</div>';
        this.currentVariantImages = [];
        return;
      }
      
      // Show ALL matching images for the variant (no limit)
      const seenImages = new Set(); // Track added images to prevent duplicates
      let addedCount = 0;
      
      matchingImages.forEach((imgData, index) => {
        // SKIP if this is the main image (already showing above)
        if (mainImageSrc && imgData.src === mainImageSrc) {
          // console.log('Skipping main image to prevent duplicate');
          return;
        }
        
        // SKIP if we already added this image
        if (seenImages.has(imgData.src)) {
          // console.log('Skipping duplicate image:', imgData.src);
          return;
        }
        
        seenImages.add(imgData.src);
        addedCount++;
        
        const thumb = document.createElement('div');
        thumb.className = 'gallery-thumb';
        thumb.style.cssText = `
          flex: 0 0 80px;
          width: 80px;
          height: 80px;
          border: 2px solid ${addedCount === 1 ? '#000' : '#e0e0e0'};
          border-radius: 6px;
          padding: 5px;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #fff;
        `;
        
        const img = document.createElement('img');
        img.src = imgData.src;
        img.alt = imgData.alt;
        img.style.cssText = `
          width: 100%;
          height: 100%;
          object-fit: contain;
        `;
        
        thumb.appendChild(img);
        
        // Click handler to change main image
        thumb.addEventListener('click', function() {
          // Update main image
          const mainImages = document.querySelectorAll(
            '.product__media img, .product-media img, [data-product-media] img'
          );
          
          if (mainImages.length > 0) {
            mainImages[0].style.transition = 'opacity 0.3s ease';
            mainImages[0].style.opacity = '0';
            
            setTimeout(() => {
              mainImages[0].src = imgData.src;
              mainImages[0].srcset = imgData.src;
              mainImages[0].style.opacity = '1';
            }, 150);
          }
          
          // CRITICAL: Update current image index for arrow navigation
          if (FrameLensThumbnails.currentVariantImages) {
            const imageIndex = FrameLensThumbnails.currentVariantImages.indexOf(imgData.src);
            if (imageIndex !== -1) {
              FrameLensThumbnails.currentImageIndex = imageIndex;
              FrameLensThumbnails.updateArrowVisibility();
            }
          }
          
          // Update active state
          galleryContainer.querySelectorAll('.gallery-thumb').forEach(t => {
            t.style.borderColor = '#e0e0e0';
          });
          this.style.borderColor = '#000';
        });
        
        // Hover effect
        thumb.addEventListener('mouseenter', function() {
          if (this.style.borderColor !== 'rgb(0, 0, 0)') {
            this.style.borderColor = '#666';
          }
        });
        
        thumb.addEventListener('mouseleave', function() {
          if (this.style.borderColor !== 'rgb(0, 0, 0)') {
            this.style.borderColor = '#e0e0e0';
          }
        });
        
        galleryContainer.appendChild(thumb);
      });
      
      // console.log('✓ Gallery thumbnails updated:', addedCount, 'shown (excluding main image)');
      
      // CRITICAL: Setup arrow navigation for current variant images
      this.setupArrowNavigation();
      
      // Ensure existing arrows stay hidden
      setTimeout(() => {
        this.hideExistingArrows();
      }, 500);
    },

    setupArrowNavigation: function() {
      // STEP 1: Hide all existing Shopify arrows
      this.hideExistingArrows();
      
      // STEP 2: Create our own working arrows
      this.createCustomArrows();
      
      // STEP 3: Update arrow visibility based on current variant
      this.updateArrowVisibility();
    },

    hideExistingArrows: function() {
      // Hide all possible existing Shopify gallery arrows - BUT NOT our custom ones
      const existingArrows = document.querySelectorAll(
        '.slideshow__btn:not(.variant-arrow-nav), ' +
        '.product__media-toggle:not(.variant-arrow-nav), ' +
        '[data-slideshow-btn]:not(.variant-arrow-nav), ' +
        '.slider-button:not(.variant-arrow-nav), ' +
        '.gallery-arrow:not(.variant-arrow-nav), ' +
        '.product-gallery__arrow:not(.variant-arrow-nav), ' +
        '.slideshow-controls__arrow:not(.variant-arrow-nav), ' +
        '.slideshow__controls button:not(.variant-arrow-nav), ' +
        '.product__media-list button:not(.variant-arrow-nav), ' +
        '.product-thumbnails button:not(.variant-arrow-nav), ' +
        'button[aria-label*="previous"]:not(.variant-arrow-nav), ' +
        'button[aria-label*="next"]:not(.variant-arrow-nav), ' +
        'button[aria-label*="Previous"]:not(.variant-arrow-nav), ' +
        'button[aria-label*="Next"]:not(.variant-arrow-nav), ' +
        '.flickity-prev-next-button:not(.variant-arrow-nav), ' +
        '.slick-arrow:not(.variant-arrow-nav), ' +
        '.swiper-button-prev:not(.variant-arrow-nav), ' +
        '.swiper-button-next:not(.variant-arrow-nav)'
      );
      
      // console.log('Hiding existing arrows:', existingArrows.length);
      
      existingArrows.forEach(arrow => {
        arrow.style.display = 'none';
        arrow.style.visibility = 'hidden';
        arrow.style.opacity = '0';
        arrow.style.pointerEvents = 'none';
      });
      
      // Also hide any arrow containers - but check they don't contain our arrows
      const arrowContainers = document.querySelectorAll(
        '.slideshow__controls, ' +
        '.product__media-controls, ' +
        '.gallery-controls, ' +
        '.slider-controls'
      );
      
      arrowContainers.forEach(container => {
        // Don't hide the entire container, just the arrows inside (except ours)
        const arrows = container.querySelectorAll('button:not(.variant-arrow-nav), .arrow:not(.variant-arrow-nav), [class*="arrow"]:not(.variant-arrow-nav)');
        arrows.forEach(arrow => {
          arrow.style.display = 'none';
        });
      });
    },

    createCustomArrows: function() {
      // Remove any existing custom arrows first
      const existingCustomArrows = document.querySelectorAll('.variant-arrow-nav');
      existingCustomArrows.forEach(arrow => arrow.remove());
      
      // Only create if we have multiple images for current variant
      if (!this.currentVariantImages || this.currentVariantImages.length <= 1) {
        return;
      }
      
      // Find main image container
      const mainImageContainer = document.querySelector(
        '.product__media-wrapper, ' +
        '.product-media-wrapper, ' +
        '.product__column--media, ' +
        '.product-single__media-wrapper, ' +
        '[data-product-media-wrapper], ' +
        'slideshow-container, ' +
        '.product__media, ' +
        '.product-media'
      );
      
      if (!mainImageContainer) {
        // console.log('Main image container not found');
        return;
      }
      
      // Make main image container relative positioned
      const computedStyle = window.getComputedStyle(mainImageContainer);
      if (computedStyle.position === 'static') {
        mainImageContainer.style.position = 'relative';
      }
      
      // Create left arrow
      const leftArrow = document.createElement('button');
      leftArrow.className = 'variant-arrow-nav variant-arrow-left';
      leftArrow.innerHTML = '‹';
      leftArrow.setAttribute('aria-label', 'Previous variant image');
      leftArrow.style.cssText = `
        position: absolute;
        left: 15px;
        top: 50%;
        transform: translateY(-50%);
        background: transparent;
        color: #000;
        border: none;
        width: 45px;
        height: 45px;
        border-radius: 50%;
        font-size: 30px;
        font-weight: bold;
        cursor: pointer;
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
      `;
      
      // Create right arrow
      const rightArrow = document.createElement('button');
      rightArrow.className = 'variant-arrow-nav variant-arrow-right';
      rightArrow.innerHTML = '›';
      rightArrow.setAttribute('aria-label', 'Next variant image');
      rightArrow.style.cssText = `
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        background: transparent;
        color: #000;
        border: none;
        width: 45px;
        height: 45px;
        border-radius: 50%;
        font-size: 30px;
        font-weight: bold;
        cursor: pointer;
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
      `;
      
      // Add click handlers
      leftArrow.addEventListener('click', (e) => {
        e.preventDefault();
        // REMOVED: e.stopPropagation() and e.stopImmediatePropagation() to allow mini cart
        this.navigateVariantImage('prev');
      });
      
      rightArrow.addEventListener('click', (e) => {
        e.preventDefault();
        // REMOVED: e.stopPropagation() and e.stopImmediatePropagation() to allow mini cart
        this.navigateVariantImage('next');
      });
      
      // Add simple hover effects
      [leftArrow, rightArrow].forEach(arrow => {
        arrow.addEventListener('mouseenter', function() {
          this.style.color = '#666';
          this.style.transform = 'translateY(-50%) scale(1.1)';
        });
        arrow.addEventListener('mouseleave', function() {
          this.style.color = '#000';
          this.style.transform = 'translateY(-50%) scale(1)';
        });
        arrow.addEventListener('mousedown', function() {
          this.style.transform = 'translateY(-50%) scale(0.95)';
        });
        arrow.addEventListener('mouseup', function() {
          this.style.transform = 'translateY(-50%) scale(1.1)';
        });
      });
      
      // Add arrows to container
      mainImageContainer.appendChild(leftArrow);
      mainImageContainer.appendChild(rightArrow);
      
      // Store references
      this.customPrevArrow = leftArrow;
      this.customNextArrow = rightArrow;
      
      // Force visibility after creation
      setTimeout(() => {
        leftArrow.style.display = 'flex';
        leftArrow.style.visibility = 'visible';
        leftArrow.style.opacity = '1';
        leftArrow.style.pointerEvents = 'auto';
        
        rightArrow.style.display = 'flex';
        rightArrow.style.visibility = 'visible';
        rightArrow.style.opacity = '1';
        rightArrow.style.pointerEvents = 'auto';
      }, 100);
      
      // console.log('✓ Custom arrows created and added');
    },

    navigateVariantImage: function(direction) {
      if (!this.currentVariantImages || this.currentVariantImages.length <= 1) {
        return;
      }
      
      // Update current index
      if (direction === 'next') {
        this.currentImageIndex = (this.currentImageIndex + 1) % this.currentVariantImages.length;
      } else {
        this.currentImageIndex = (this.currentImageIndex - 1 + this.currentVariantImages.length) % this.currentVariantImages.length;
      }
      
      // Get new image URL
      const newImageSrc = this.currentVariantImages[this.currentImageIndex];
      
      // Update main image
      const mainImages = document.querySelectorAll(
        '.product__media img, .product-media img, [data-product-media] img'
      );
      
      if (mainImages.length > 0) {
        mainImages[0].style.transition = 'opacity 0.3s ease';
        mainImages[0].style.opacity = '0';
        
        setTimeout(() => {
          mainImages[0].src = newImageSrc;
          mainImages[0].srcset = newImageSrc;
          mainImages[0].style.opacity = '1';
        }, 150);
      }
      
      // Update thumbnail active state
      const thumbnails = document.querySelectorAll('.gallery-thumb');
      thumbnails.forEach((thumb, index) => {
        const img = thumb.querySelector('img');
        if (img && img.src === newImageSrc) {
          thumb.style.borderColor = '#000';
        } else {
          thumb.style.borderColor = '#e0e0e0';
        }
      });
      
      // Update arrow visibility
      this.updateArrowVisibility();
      
      // Setup keyboard navigation
      this.setupKeyboardNavigation();
    },

    setupKeyboardNavigation: function() {
      // Remove existing keyboard listener
      if (this.keyboardHandler) {
        document.removeEventListener('keydown', this.keyboardHandler);
      }
      
      // Add keyboard navigation (left/right arrows)
      this.keyboardHandler = (e) => {
        // Only work when main image area is focused or no input is focused
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA' || 
          activeElement.tagName === 'SELECT' ||
          activeElement.contentEditable === 'true'
        );
        
        if (isInputFocused) {
          return; // Don't interfere with form inputs
        }
        
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.navigateVariantImage('prev');
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.navigateVariantImage('next');
        }
      };
      
      document.addEventListener('keydown', this.keyboardHandler);
    },

    updateArrowVisibility: function() {
      // Handle our custom arrows
      const leftArrow = this.customPrevArrow || document.querySelector('.variant-arrow-left');
      const rightArrow = this.customNextArrow || document.querySelector('.variant-arrow-right');
      
      if (!leftArrow || !rightArrow) {
        return;
      }
      
      // Show/hide arrows based on current variant images
      if (!this.currentVariantImages || this.currentVariantImages.length <= 1) {
        leftArrow.style.display = 'none';
        rightArrow.style.display = 'none';
      } else {
        // Force show our arrows
        leftArrow.style.display = 'flex';
        leftArrow.style.visibility = 'visible';
        leftArrow.style.pointerEvents = 'auto';
        
        rightArrow.style.display = 'flex';
        rightArrow.style.visibility = 'visible';
        rightArrow.style.pointerEvents = 'auto';
        
        // Optional: Fade arrows at start/end positions
        if (this.currentImageIndex === 0) {
          leftArrow.style.opacity = '0.5';
        } else {
          leftArrow.style.opacity = '1';
        }
        
        if (this.currentImageIndex === this.currentVariantImages.length - 1) {
          rightArrow.style.opacity = '0.5';
        } else {
          rightArrow.style.opacity = '1';
        }
      }
    },

    showSelectedText: function(text, picker) {
      // console.log('=== SHOWING SELECTED TEXT ===');
      // console.log('Input text:', text);
      
      // IMPORTANT: The text parameter is the COMPLETE option1 value from variant
      // Examples:
      // "Black - Grey - V2" → Frame: "Black", Lens: "Grey - V2"
      // "Black - Grey" → Frame: "Black", Lens: "Grey"
      // "White - -" → Frame: "White", Lens: "-"
      // "Havana - Green" → Frame: "Havana", Lens: "Green"
      
      // Split on FIRST dash only to separate Frame from Lens
      const firstDashIndex = text.indexOf(' - ');
      let frameColor = text;
      let lensColor = '-';
      
      if (firstDashIndex !== -1) {
        frameColor = text.substring(0, firstDashIndex).trim();
        lensColor = text.substring(firstDashIndex + 3).trim(); // +3 to skip " - "
      }
      
      // If lens is empty or just whitespace, use dash
      if (!lensColor || lensColor === '') {
        lensColor = '-';
      }
      
      // console.log('Frame:', frameColor);
      // console.log('Lens:', lensColor);
      // console.log('Complete variant name:', text);
      
      // Detect if lens is Polarized
      const isPolarized = lensColor.toLowerCase().includes('polar');
      
      // Find or create container for Frame, Lens display (ABOVE thumbnails)
      let displayContainer = document.querySelector('.variant-selection-display');
      
      if (!displayContainer) {
        displayContainer = document.createElement('div');
        displayContainer.className = 'variant-selection-display';
        displayContainer.style.cssText = 'margin: 15px 0 10px 0; font-size: 14px; line-height: 1.8;';
        
        // Insert before thumbnails
        const thumbnailContainer = document.querySelector('.frame-lens-visual-selector');
        if (thumbnailContainer && thumbnailContainer.parentNode) {
          thumbnailContainer.parentNode.insertBefore(displayContainer, thumbnailContainer);
        }
      }
      
      // Always show Frame and Lens (even if lens is dash)
      displayContainer.innerHTML = `
        <div style="margin-bottom: 6px;">
          <span style="font-weight: 400; color: #666;">Frame:</span> 
          <span style="font-weight: 600; color: #000;">${frameColor}</span>
        </div>
        <div style="margin-bottom: 6px;">
          <span style="font-weight: 400; color: #666;">Lens:</span> 
          <span style="font-weight: 600; color: #000;">${lensColor}</span>
        </div>
      `;
      
      // Find or create Size display container (BELOW thumbnails)
      let sizeDisplayContainer = document.querySelector('.size-selection-display');
      
      if (!sizeDisplayContainer) {
        sizeDisplayContainer = document.createElement('div');
        sizeDisplayContainer.className = 'size-selection-display';
        sizeDisplayContainer.style.cssText = 'margin: 15px 0 10px 0; font-size: 14px; line-height: 1.8;';
        
        // Insert after thumbnails
        const thumbnailContainer = document.querySelector('.frame-lens-visual-selector');
        if (thumbnailContainer && thumbnailContainer.parentNode) {
          thumbnailContainer.parentNode.insertBefore(sizeDisplayContainer, thumbnailContainer.nextSibling);
        }
      }
      
      // Get available sizes for this Frame-Lens combination
      const availableSizes = this.getAvailableSizesForFrameLens(text);
      // console.log('Available sizes for', text, ':', availableSizes);
      
      // Get current size selection
      const sizeSelect = document.querySelector('select[name*="Size"], select[name*="size"], select[data-option*="Size"]');
      const sizeRadio = document.querySelector('input[type="radio"][name*="Size"]:checked, input[type="radio"][name*="size"]:checked');
      let currentSize = null;
      
      if (sizeSelect && sizeSelect.value) {
        currentSize = sizeSelect.value;
      } else if (sizeRadio) {
        const sizeLabel = document.querySelector(`label[for="${sizeRadio.id}"]`);
        currentSize = sizeLabel ? sizeLabel.textContent.trim() : sizeRadio.value;
      }
      
      // If no size selected, use first available
      if (!currentSize && availableSizes.length > 0) {
        currentSize = availableSizes[0];
      }
      
      // Update Size display (below thumbnails)
      // If multiple sizes available, show interactive selector
      if (availableSizes.length > 1) {
        // Multiple sizes - show as clickable buttons
        let sizeButtonsHTML = '<div style="margin-bottom: 6px;"><span style="font-weight: 400; color: #666;">Size:</span></div>';
        sizeButtonsHTML += '<div class="size-selector-buttons" style="display: flex; gap: 10px; margin-top: 8px;">';
        
        availableSizes.forEach(size => {
          const isActive = size === currentSize;
          sizeButtonsHTML += `
            <button type="button" 
                    class="size-option-btn ${isActive ? 'active' : ''}" 
                    data-size="${size}"
                    style="
                      padding: 8px 16px;
                      border: 2px solid ${isActive ? '#000' : '#ddd'};
                      background: ${isActive ? '#000' : '#fff'};
                      color: ${isActive ? '#fff' : '#000'};
                      border-radius: 4px;
                      cursor: pointer;
                      font-weight: 600;
                      font-size: 14px;
                      transition: all 0.2s ease;
                    ">
              ${size}
            </button>
          `;
        });
        
        sizeButtonsHTML += '</div>';
        sizeDisplayContainer.innerHTML = sizeButtonsHTML;
        
        // Add click handlers to size buttons
        setTimeout(() => {
          const sizeButtons = sizeDisplayContainer.querySelectorAll('.size-option-btn');
          sizeButtons.forEach(btn => {
            btn.addEventListener('click', function(e) {
              e.preventDefault();
              // REMOVED: e.stopPropagation() and e.stopImmediatePropagation() to allow mini cart
              
              const selectedSize = this.dataset.size;
              // console.log('=== SIZE BUTTON CLICKED ===');
              // console.log('Selected Size:', selectedSize);
              // console.log('Frame-Lens:', text);
              
              // Update active state
              sizeButtons.forEach(b => {
                b.classList.remove('active');
                b.style.border = '2px solid #ddd';
                b.style.background = '#fff';
                b.style.color = '#000';
              });
              this.classList.add('active');
              this.style.border = '2px solid #000';
              this.style.background = '#000';
              this.style.color = '#fff';
              
              // Find matching variant using variantDataMap or ShopifyAnalytics
              let variantId = null;
              let variantPrice = null;
              
              // Strategy 1: Use variantDataMap
              if (window.variantDataMap) {
                const fullKey = `${text} / ${selectedSize}`;
                const variantData = window.variantDataMap[fullKey];
                
                if (variantData) {
                  variantId = variantData.id;
                  variantPrice = variantData.price;
                  // console.log('✓ Found variant in map:', fullKey);
                  // console.log('  Variant ID:', variantId);
                  // console.log('  Price:', variantPrice);
                }
              }
              
              // Strategy 2: Use ShopifyAnalytics
              if (!variantId && typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
                const product = ShopifyAnalytics.meta.product;
                const matchingVariant = product.variants.find(v => {
                  const vOption1 = (v.option1 || '').trim();
                  const vOption2 = (v.option2 || '').trim();
                  return vOption1 === text && vOption2 === selectedSize;
                });
                
                if (matchingVariant) {
                  variantId = matchingVariant.id;
                  variantPrice = matchingVariant.price;
                  // console.log('✓ Found variant in ShopifyAnalytics:', matchingVariant.public_title);
                  // console.log('  Variant ID:', variantId);
                  // console.log('  Price:', variantPrice);
                }
              }
              
              if (!variantId) {
                console.error('✗ Could not find matching variant for:', text, '/', selectedSize);
                return;
              }
              
              // CRITICAL: Update variant selector WITHOUT triggering change events that might hide thumbnails
              const variantSelect = document.querySelector('select[name="id"]');
              if (variantSelect) {
                // Temporarily disable all event listeners
                const oldValue = variantSelect.value;
                variantSelect.value = variantId;
                
                // Only trigger change if value actually changed
                if (oldValue !== variantId) {
                  // Create a custom event with a flag to prevent thumbnail hiding
                  const changeEvent = new Event('change', { bubbles: true });
                  changeEvent.preventThumbnailHide = true;
                  variantSelect.dispatchEvent(changeEvent);
                }
                
                // console.log('✓ Variant select updated to:', variantId);
              }
              
              // Update hidden input
              const hiddenInput = document.querySelector('input[name="id"][type="hidden"]');
              if (hiddenInput) {
                hiddenInput.value = variantId;
              }
              
              // Update hidden size selector SILENTLY (no events)
              if (sizeSelect) {
                sizeSelect.value = selectedSize;
                // Don't trigger change event - it might cause issues
              } else {
                // Find and check the radio button SILENTLY
                const sizeRadios = document.querySelectorAll('input[type="radio"][name*="Size"], input[type="radio"][name*="size"]');
                sizeRadios.forEach(radio => {
                  const label = document.querySelector(`label[for="${radio.id}"]`);
                  const labelText = label ? label.textContent.trim() : radio.value;
                  radio.checked = (labelText === selectedSize);
                  // Don't trigger change event
                });
              }
              
              // Update price directly
              if (variantPrice) {
                FrameLensThumbnails.updatePriceDirectly(variantPrice);
              } else if (variantId) {
                FrameLensThumbnails.updatePriceByVariantId(variantId);
              }
              
              // Update URL
              if (variantId) {
                FrameLensThumbnails.updateURL(variantId);
              }
              
              // console.log('=== SIZE CHANGE COMPLETE ===');
              // console.log('✓ Thumbnails should remain visible');
            });
            
            // Hover effects
            btn.addEventListener('mouseenter', function() {
              if (!this.classList.contains('active')) {
                this.style.borderColor = '#666';
              }
            });
            btn.addEventListener('mouseleave', function() {
              if (!this.classList.contains('active')) {
                this.style.borderColor = '#ddd';
              }
            });
          });
        }, 100);
        
      } else if (availableSizes.length === 1) {
        // Single size - just show as text
        sizeDisplayContainer.innerHTML = `
          <div style="margin-bottom: 6px;">
            <span style="font-weight: 400; color: #666;">Size:</span> 
            <span style="font-weight: 600; color: #000;">${availableSizes[0]}</span>
          </div>
        `;
      } else {
        // No sizes found - show current or default
        sizeDisplayContainer.innerHTML = `
          <div style="margin-bottom: 6px;">
            <span style="font-weight: 400; color: #666;">Size:</span> 
            <span style="font-weight: 600; color: #000;">${currentSize || 'N/A'}</span>
          </div>
        `;
      }
      
      // Check if both Polarized and Non-Polarized variants exist for this frame
      this.checkAndShowLensTypeOptions(frameColor, sizeDisplayContainer, isPolarized);
      
      // Hide original "Frame-Lens" label
      const frameLensLabel = picker.querySelector('legend, .product-form__label');
      if (frameLensLabel) {
        frameLensLabel.style.display = 'none';
      }
      
      // console.log('✓ Display updated - Frame:', frameColor, 'Lens:', lensColor, 'Size:', currentSize, 'Polarized:', isPolarized);
    },

    checkAndShowLensTypeOptions: function(frameColor, container, currentIsPolarized) {
      // Get all variants for this frame to check if both Polarized and Non-Polarized exist
      const allInputs = document.querySelectorAll('.variant-picker input[type="radio"]');
      let hasPolarized = false;
      let hasNonPolarized = false;
      
      allInputs.forEach(input => {
        const label = document.querySelector(`label[for="${input.id}"]`);
        const optionText = label ? label.textContent.trim() : input.value;
        
        // Check if this variant matches the current frame
        if (optionText.toLowerCase().includes(frameColor.toLowerCase())) {
          const parts = optionText.split('-').map(p => p.trim());
          const lensColor = parts[1] || '';
          
          if (lensColor.toLowerCase().includes('polar')) {
            hasPolarized = true;
          } else if (lensColor) {
            hasNonPolarized = true;
          }
        }
      });
      
      // Remove any existing lens type section
      const existingLensType = container.querySelector('.lens-type-section');
      if (existingLensType) {
        existingLensType.remove();
      }
      
      // Case 1: Both types exist - show selectable buttons
      // COMMENTED OUT - Not needed for now
      /*
      if (hasPolarized && hasNonPolarized) {
        const lensTypeHtml = `
          <div style="margin-top: 10px;">
            <span style="font-weight: 400; color: #666; display: block; margin-bottom: 8px;">Lens Type:</span>
            <div style="display: flex; gap: 10px;">
              <button class="lens-type-btn ${currentIsPolarized ? 'active' : ''}" data-type="polarized" 
                style="padding: 8px 16px; border: 2px solid ${currentIsPolarized ? '#000' : '#e0e0e0'}; 
                background: ${currentIsPolarized ? '#f8f8f8' : '#fff'}; border-radius: 6px; cursor: pointer; 
                font-size: 14px; font-weight: ${currentIsPolarized ? '600' : '400'}; color: #000;">
                Polarized
              </button>
              <button class="lens-type-btn ${!currentIsPolarized ? 'active' : ''}" data-type="non-polarized" 
                style="padding: 8px 16px; border: 2px solid ${!currentIsPolarized ? '#000' : '#e0e0e0'}; 
                background: ${!currentIsPolarized ? '#f8f8f8' : '#fff'}; border-radius: 6px; cursor: pointer; 
                font-size: 14px; font-weight: ${!currentIsPolarized ? '600' : '400'}; color: #000;">
                Non-Polarized
              </button>
            </div>
          </div>
        `;
        
        const tempDiv = document.createElement('div');
        tempDiv.className = 'lens-type-section';
        tempDiv.innerHTML = lensTypeHtml;
        container.appendChild(tempDiv);
        
        // Add click handlers for lens type buttons
        setTimeout(() => {
          const lensTypeBtns = container.querySelectorAll('.lens-type-btn');
          lensTypeBtns.forEach(btn => {
            btn.addEventListener('click', function(e) {
              e.preventDefault();
              const selectedType = this.dataset.type;
              FrameLensThumbnails.switchLensType(frameColor, selectedType);
            });
          });
        }, 100);
      }
      */
      
      // Case 2: Only Polarized exists - show text only
      // if (hasPolarized && !hasNonPolarized) {
      //   const lensTypeHtml = `
      //     <div style="margin-top: 10px;">
      //       <span style="font-weight: 400; color: #666;">Lens Type:</span> 
      //       <span style="font-weight: 600; color: #000;">Polarized</span>
      //     </div>
      //   `;
        
      //   const tempDiv = document.createElement('div');
      //   tempDiv.className = 'lens-type-section';
      //   tempDiv.innerHTML = lensTypeHtml;
      //   container.appendChild(tempDiv);
      // }
      // Case 3: Only Non-Polarized or no lens info - show nothing
      else {
        // Don't show anything
      }
    },

    switchLensType: function(frameColor, lensType) {
      // console.log('Switching lens type:', lensType, 'for frame:', frameColor);
      
      // Find the matching variant with the selected lens type
      const allInputs = document.querySelectorAll('.variant-picker input[type="radio"]');
      
      allInputs.forEach(input => {
        const label = document.querySelector(`label[for="${input.id}"]`);
        const optionText = label ? label.textContent.trim() : input.value;
        
        // Check if this variant matches the frame and lens type
        if (optionText.toLowerCase().includes(frameColor.toLowerCase())) {
          const parts = optionText.split('-').map(p => p.trim());
          const lensColor = parts[1] || '';
          
          const isPolarized = lensColor.toLowerCase().includes('polar');
          
          if ((lensType === 'polarized' && isPolarized) || 
              (lensType === 'non-polarized' && !isPolarized && lensColor)) {
            // Found matching variant - click it
            // console.log('✓ Switching to:', optionText);
            
            // Find and click the corresponding thumbnail
            const thumbnails = document.querySelectorAll('.frame-lens-option');
            thumbnails.forEach(thumb => {
              const thumbLabel = thumb.querySelector('.frame-lens-label');
              if (thumbLabel && thumbLabel.textContent === optionText) {
                thumb.click();
              }
            });
            
            return;
          }
        }
      });
    },

    getAvailableSizesForFrameLens: function(frameLensText) {
      const availableSizes = [];
      
      // STRATEGY 1: Use variantDataMap from Liquid template
      if (window.variantDataMap) {
        Object.keys(window.variantDataMap).forEach(key => {
          // Keys are like "Black - Green / L" or "Black - Green / XXL"
          if (key.startsWith(frameLensText + ' / ')) {
            const size = key.split(' / ')[1];
            if (size && !availableSizes.includes(size)) {
              availableSizes.push(size);
            }
          }
        });
        
        if (availableSizes.length > 0) {
          // console.log('✓ Found sizes from variantDataMap:', availableSizes);
          return availableSizes;
        }
      }
      
      // STRATEGY 2: Use ShopifyAnalytics
      if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        const product = ShopifyAnalytics.meta.product;
        
        product.variants.forEach(variant => {
          const vOption1 = (variant.option1 || '').trim();
          const vOption2 = (variant.option2 || '').trim();
          const vOption3 = (variant.option3 || '').trim();
          
          // If option1 matches Frame-Lens, get the size (option2 or option3)
          if (vOption1 === frameLensText) {
            const size = vOption2 || vOption3;
            if (size && !availableSizes.includes(size)) {
              availableSizes.push(size);
            }
          }
        });
        
        if (availableSizes.length > 0) {
          // console.log('✓ Found sizes from ShopifyAnalytics:', availableSizes);
          return availableSizes;
        }
      }
      
      // STRATEGY 3: Get all available sizes from DOM
      const sizeSelect = document.querySelector('select[name*="Size"], select[name*="size"]');
      if (sizeSelect) {
        Array.from(sizeSelect.options).forEach(option => {
          if (option.value && option.value !== '' && !option.disabled) {
            availableSizes.push(option.value);
          }
        });
      } else {
        const sizeRadios = document.querySelectorAll('input[type="radio"][name*="Size"], input[type="radio"][name*="size"]');
        sizeRadios.forEach(radio => {
          if (!radio.disabled) {
            const label = document.querySelector(`label[for="${radio.id}"]`);
            const size = label ? label.textContent.trim() : radio.value;
            if (size && !availableSizes.includes(size)) {
              availableSizes.push(size);
            }
          }
        });
      }
      
      // console.log('✓ Found sizes from DOM:', availableSizes);
      return availableSizes;
    },

    selectMatchingVariantWithSize: function(variantText) {
      // console.log('=== SELECTING VARIANT WITH SIZE ===');
      // console.log('Frame-Lens:', variantText);
      
      // Get current size
      const sizeSelect = document.querySelector('select[name*="Size"], select[name*="size"]');
      const sizeRadio = document.querySelector('input[type="radio"][name*="Size"]:checked, input[type="radio"][name*="size"]:checked');
      let currentSize = null;
      
      if (sizeSelect && sizeSelect.value) {
        currentSize = sizeSelect.value;
      } else if (sizeRadio) {
        const sizeLabel = document.querySelector(`label[for="${sizeRadio.id}"]`);
        currentSize = sizeLabel ? sizeLabel.textContent.trim() : sizeRadio.value;
      }
      
      // If no size selected, find first available size for this Frame-Lens combination
      if (!currentSize) {
        // console.log('No size selected, finding first available size...');
        
        // Get all available sizes for this Frame-Lens
        const availableSizes = this.getAvailableSizesForFrameLens(variantText);
        // console.log('Available sizes:', availableSizes);
        
        if (availableSizes.length > 0) {
          currentSize = availableSizes[0];
          // console.log('Auto-selected first available size:', currentSize);
          
          // Auto-select this size in the UI
          if (sizeSelect) {
            sizeSelect.value = currentSize;
            sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            // Find and check the radio button for this size
            const sizeRadios = document.querySelectorAll('input[type="radio"][name*="Size"], input[type="radio"][name*="size"]');
            sizeRadios.forEach(radio => {
              const label = document.querySelector(`label[for="${radio.id}"]`);
              const labelText = label ? label.textContent.trim() : radio.value;
              if (labelText === currentSize) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          }
        } else {
          console.error('No available sizes found for:', variantText);
          return;
        }
      }
      
      // console.log('Current Size:', currentSize);
      
      // STRATEGY 1: Use variantDataMap from Liquid template (most accurate)
      if (window.variantDataMap) {
        // Try with full key first: "Black - Grey / M"
        const fullKey = `${variantText} / ${currentSize}`;
        let variantData = window.variantDataMap[fullKey];
        
        // If not found, try just Frame-Lens: "Black - Grey"
        if (!variantData) {
          variantData = window.variantDataMap[variantText];
        }
        
        if (variantData) {
          // console.log('✓✓✓ FOUND IN VARIANT DATA MAP ✓✓✓');
          // console.log('Key used:', fullKey in window.variantDataMap ? fullKey : variantText);
          // console.log('Variant ID:', variantData.id);
          // console.log('Price:', `$${(variantData.price / 100).toFixed(2)}`);
          
          // Store current variant
          FrameLensThumbnails.currentVariantId = variantData.id;
          
          // Update variant select
          const variantSelect = document.querySelector('select[name="id"]');
          if (variantSelect) {
            variantSelect.value = variantData.id;
            variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
            // console.log('✓ Variant select updated');
          }
          
          // Update hidden input
          const hiddenInput = document.querySelector('input[name="id"][type="hidden"]');
          if (hiddenInput) {
            hiddenInput.value = variantData.id;
          }
          
          // Update price using VARIANT ID (most accurate method)
          this.updatePriceByVariantId(variantData.id);
          
          // Update URL
          this.updateURL(variantData.id);
          
          // console.log('=== VARIANT SELECTION COMPLETE ===');
          return;
        }
      }
      
      // STRATEGY 2: Use ShopifyAnalytics (fallback)
      // console.log('⚠️ Variant data map not found, using ShopifyAnalytics...');
      
      if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        const product = ShopifyAnalytics.meta.product;
        
        // Find exact match: Frame-Lens + Size
        const matchingVariant = product.variants.find(v => {
          const vTitle = (v.public_title || v.name || '').trim();
          const vOption1 = (v.option1 || '').trim();
          
          // Try multiple matching strategies
          // Strategy 1: Exact title match "Black - Grey - V2 / M"
          const expectedTitle = `${variantText} / ${currentSize}`;
          if (vTitle === expectedTitle) {
            // console.log('✓ Exact title match:', vTitle);
            return true;
          }
          
          // Strategy 2: Option1 matches Frame-Lens exactly
          if (vOption1 === variantText) {
            // Check if size matches (option2 or option3)
            const vOption2 = (v.option2 || '').trim();
            const vOption3 = (v.option3 || '').trim();
            if (vOption2 === currentSize || vOption3 === currentSize) {
              // console.log('✓ Option match:', vOption1, '/', currentSize);
              return true;
            }
          }
          
          // Strategy 3: Title contains both Frame-Lens and Size
          if (vTitle.includes(variantText) && vTitle.includes(`/ ${currentSize}`)) {
            // console.log('✓ Partial match:', vTitle);
            return true;
          }
          
          return false;
        });
        
        if (matchingVariant) {
          // console.log('✓✓✓ EXACT MATCH FOUND ✓✓✓');
          // console.log('Variant:', matchingVariant.public_title || matchingVariant.name);
          // console.log('ID:', matchingVariant.id);
          // console.log('Price:', `$${(matchingVariant.price / 100).toFixed(2)}`);
          
          // Store current variant
          FrameLensThumbnails.currentVariantId = matchingVariant.id;
          
          // Update variant select
          const variantSelect = document.querySelector('select[name="id"]');
          if (variantSelect) {
            variantSelect.value = matchingVariant.id;
            variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
            // console.log('✓ Variant select set to:', matchingVariant.id);
          }
          
          // Update hidden input if exists
          const hiddenInput = document.querySelector('input[name="id"][type="hidden"]');
          if (hiddenInput) {
            hiddenInput.value = matchingVariant.id;
            // console.log('✓ Hidden input set to:', matchingVariant.id);
          }
          
          // Update price using VARIANT ID (most accurate)
          this.updatePriceByVariantId(matchingVariant.id);
          
          // Update URL
          this.updateURL(matchingVariant.id);
          
          // console.log('=== VARIANT SELECTION COMPLETE ===');
        } else {
          console.error('✗✗✗ NO EXACT MATCH FOUND ✗✗✗');
          // console.log('Looking for:', variantText, '/', currentSize);
          // console.log('Available variants:');
          product.variants.forEach(v => {
            // console.log(' -', v.public_title || v.name, '(ID:', v.id, ')');
          });
        }
      }
    },

    updatePriceDirectly: function(priceInCents) {
      const formattedPrice = `$${(priceInCents / 100).toFixed(2)} USD`;
      
      // console.log('=== UPDATING PRICE DIRECTLY ===');
      // console.log('Price in cents:', priceInCents);
      // console.log('Formatted price:', formattedPrice);
      
      const priceElements = document.querySelectorAll(
        '.price__regular .price-item, ' +
        '.product-price, ' +
        '[data-product-price], ' +
        '.price-item--regular, ' +
        '.price__sale .price-item--regular, ' +
        '[data-price], ' +
        '.current-price, ' +
        'span.price, ' +
        '.price, ' +
        '[data-sezzleindex]'
      );
      
      // console.log('Found', priceElements.length, 'price elements');
      
      priceElements.forEach((el, index) => {
        const oldPrice = el.textContent;
        el.textContent = formattedPrice;
        // console.log(`${index + 1}. Updated "${el.className || el.tagName}" from "${oldPrice}" to "${formattedPrice}"`);
      });
      
      // Also update any price in ref="priceContainer"
      const priceContainers = document.querySelectorAll('[ref="priceContainer"], [data-ref="priceContainer"]');
      priceContainers.forEach((container, index) => {
        const priceSpan = container.querySelector('span.price, .price, [data-sezzleindex]');
        if (priceSpan) {
          const oldPrice = priceSpan.textContent;
          priceSpan.textContent = formattedPrice;
          // console.log(`${index + 1}. Container price updated from "${oldPrice}" to "${formattedPrice}"`);
        }
      });
      
      // console.log('✓✓✓ PRICE UPDATE COMPLETE ✓✓✓');
    },

    updateSezzlePrice: function(priceInCents) {
      const formattedPrice = `$${(priceInCents / 100).toFixed(2)} USD`;
      
      // console.log('=== UPDATING SEZZLE PRICE SPECIFICALLY ===');
      // console.log('Target price:', formattedPrice);
      
      // Target the specific Sezzle price element in ref="priceContainer"
      const priceContainers = document.querySelectorAll('[ref="priceContainer"]');
      
      // console.log('Found', priceContainers.length, 'price containers with ref="priceContainer"');
      
      priceContainers.forEach((container, index) => {
        // Find the price span inside
        const priceSpan = container.querySelector('span.price[data-sezzleindex]');
        
        if (priceSpan) {
          const oldPrice = priceSpan.textContent;
          priceSpan.textContent = formattedPrice;
          // console.log(`✓ Container ${index + 1}: Updated from "${oldPrice}" to "${formattedPrice}"`);
        } else {
          // Try without data-sezzleindex
          const anyPriceSpan = container.querySelector('span.price, .price');
          if (anyPriceSpan) {
            const oldPrice = anyPriceSpan.textContent;
            anyPriceSpan.textContent = formattedPrice;
            // console.log(`✓ Container ${index + 1} (fallback): Updated from "${oldPrice}" to "${formattedPrice}"`);
          }
        }
      });
      
      // Also directly target any element with data-sezzleindex
      const sezzleElements = document.querySelectorAll('[data-sezzleindex]');
      // console.log('Found', sezzleElements.length, 'elements with data-sezzleindex');
      
      sezzleElements.forEach((el, index) => {
        const oldPrice = el.textContent;
        el.textContent = formattedPrice;
        // console.log(`✓ Sezzle element ${index + 1}: Updated from "${oldPrice}" to "${formattedPrice}"`);
      });
      
      // console.log('✓✓✓ SEZZLE PRICE UPDATE COMPLETE ✓✓✓');
    },

    updatePriceByVariantId: function(variantId) {
      // console.log('=== UPDATING PRICE BY VARIANT ID ===');
      // console.log('Variant ID:', variantId);
      
      // Get variant data from hidden Liquid template div
      const variantData = document.querySelector(
        `#variant-images-data .custom-variant-image[data-variant-id="${variantId}"]`
      );
      
      if (!variantData) {
        console.error('✗ Variant data not found for ID:', variantId);
        return;
      }
      
      const price = variantData.dataset.variantPrice;
      const comparePrice = variantData.dataset.variantComparePrice;
      const variantTitle = variantData.dataset.variantTitle;
      
      // console.log('Variant Title:', variantTitle);
      // console.log('Price (cents):', price);
      // console.log('Compare Price (cents):', comparePrice);
      
      if (!price) {
        console.error('✗ Price not found in variant data');
        return;
      }
      
      // Format price with USD
      const formattedPrice = `$${(parseInt(price) / 100).toFixed(2)} USD`;
      const formattedComparePrice = comparePrice ? `$${(parseInt(comparePrice) / 100).toFixed(2)} USD` : null;
      
      // console.log('Formatted Price:', formattedPrice);
      if (formattedComparePrice) {
        // console.log('Formatted Compare Price:', formattedComparePrice);
      }
      
      // Update regular price
      const priceElements = document.querySelectorAll(
        '.price__regular .price-item, ' +
        '.price__regular .price-item--regular, ' +
        '.product-price, ' +
        '[data-product-price], ' +
        '.price-item--regular, ' +
        '[data-price], ' +
        '.current-price, ' +
        'span.price, ' +
        '.price, ' +
        '[data-sezzleindex]'
      );
      
      // console.log('Found', priceElements.length, 'price elements to update');
      
      priceElements.forEach((el, index) => {
        const oldPrice = el.textContent;
        el.textContent = formattedPrice;
        // console.log(`${index + 1}. Updated "${el.className || el.tagName}" from "${oldPrice}" to "${formattedPrice}"`);
      });
      
      // Update compare price (sale price) if exists
      if (formattedComparePrice && parseInt(comparePrice) > parseInt(price)) {
        const compareElements = document.querySelectorAll(
          '.price__sale .price-item--regular, ' +
          '.price__compare, ' +
          '.compare-price, ' +
          '[data-compare-price]'
        );
        
        // console.log('Found', compareElements.length, 'compare price elements');
        
        compareElements.forEach((el, index) => {
          const oldPrice = el.textContent;
          el.textContent = formattedComparePrice;
          // console.log(`Compare ${index + 1}. Updated from "${oldPrice}" to "${formattedComparePrice}"`);
        });
      }
      
      // Update Sezzle price containers
      const priceContainers = document.querySelectorAll('[ref="priceContainer"]');
      priceContainers.forEach((container, index) => {
        const priceSpan = container.querySelector('span.price, .price, [data-sezzleindex]');
        if (priceSpan) {
          const oldPrice = priceSpan.textContent;
          priceSpan.textContent = formattedPrice;
          // console.log(`Container ${index + 1}: Updated from "${oldPrice}" to "${formattedPrice}"`);
        }
      });
      
      // console.log('✓✓✓ PRICE UPDATE BY VARIANT ID COMPLETE ✓✓✓');
    },

    selectMatchingVariant: function(variantText) {
      // console.log('Selecting matching variant for:', variantText);
      
      if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        const product = ShopifyAnalytics.meta.product;
        
        const matchingVariant = product.variants.find(v => {
          const vName = (v.public_title || v.name || '').toLowerCase();
          const searchText = variantText.toLowerCase();
          return vName.includes(searchText) || searchText.includes(vName) || vName === searchText;
        });
        
        if (matchingVariant) {
          // console.log('✓ Found matching variant:', matchingVariant.id);
          
          const variantSelect = document.querySelector('select[name="id"]');
          if (variantSelect) {
            variantSelect.value = matchingVariant.id;
            variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
            // console.log('✓ Variant select updated');
          }
          
          this.updatePrice(matchingVariant);
          this.updateURL(matchingVariant.id);
          
          if (matchingVariant.featured_image) {
            this.updateMainProductImage(matchingVariant.featured_image.src, variantText);
          }
        } else {
          // console.log('✗ No matching variant found');
        }
      }
    },

    updatePrice: function(variant) {
      if (variant.price) {
        // Try multiple price element selectors
        const priceElements = document.querySelectorAll(
          '.price__regular .price-item, ' +
          '.product-price, ' +
          '[data-product-price], ' +
          '.price-item--regular, ' +
          '.price__sale .price-item--regular, ' +
          '[data-price], ' +
          '.product__price .price, ' +
          '.price-item.price-item--regular, ' +
          'span.price, ' +
          '.current-price, ' +
          '[itemprop="price"]'
        );
        
        // Format price properly with $ sign
        const formattedPrice = `$${(variant.price / 100).toFixed(2)}`;
        
        // console.log('Trying to update', priceElements.length, 'price elements');
        
        priceElements.forEach((el, index) => {
          // console.log(`Element ${index}:`, el.className, 'Current text:', el.textContent);
          el.textContent = formattedPrice;
        });
        
        // Also try updating by text content matching (find elements with $ and numbers)
        const allElements = document.querySelectorAll('*');
        allElements.forEach(el => {
          const text = el.textContent.trim();
          // Match $XXX.XX USD pattern
          if (text.match(/^\$\d+\.\d{2}\s*(USD)?$/) && el.children.length === 0) {
            // console.log('Found price by pattern:', el.className, text);
            el.textContent = `${formattedPrice} USD`;
          }
        });
        
        // console.log('✓ Price updated to:', formattedPrice, 'for variant:', variant.id);
      }
    },

    updatePriceByName: function(variantName) {
      // console.log('=== Updating Price for:', variantName, '===');
      
      // Get current size selection
      const sizeSelect = document.querySelector('select[name*="Size"], select[name*="size"]');
      const sizeRadio = document.querySelector('input[type="radio"][name*="Size"]:checked, input[type="radio"][name*="size"]:checked');
      let currentSize = 'M'; // Default
      
      if (sizeSelect && sizeSelect.value) {
        currentSize = sizeSelect.value;
      } else if (sizeRadio) {
        const sizeLabel = document.querySelector(`label[for="${sizeRadio.id}"]`);
        currentSize = sizeLabel ? sizeLabel.textContent.trim() : sizeRadio.value;
      }
      
      // console.log('Current Size:', currentSize);
      
      // Find variant by Frame-Lens name + Size
      if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        const product = ShopifyAnalytics.meta.product;
        
        if (product.variants) {
          // Try to find exact match with Frame-Lens + Size
          let matchingVariant = product.variants.find(v => {
            const vTitle = (v.public_title || v.name || '').trim();
            const vOption1 = (v.option1 || '').trim();
            
            // Check if Frame-Lens matches and Size matches
            const frameLensMatches = vTitle.includes(variantName) || 
                                    variantName.includes(vTitle) ||
                                    vOption1 === variantName;
            
            const sizeMatches = vTitle.includes(`/ ${currentSize}`) || 
                               vTitle.endsWith(currentSize);
            
            return frameLensMatches && sizeMatches;
          });
          
          // If no exact match, try just Frame-Lens name
          if (!matchingVariant) {
            matchingVariant = product.variants.find(v => {
              const vTitle = (v.public_title || v.name || '').trim();
              const vOption1 = (v.option1 || '').trim();
              
              return vTitle === variantName || 
                     vOption1 === variantName ||
                     vTitle.includes(variantName);
            });
          }
          
          if (matchingVariant) {
            // console.log('✓ Found variant:', matchingVariant.public_title || matchingVariant.name);
            // console.log('  ID:', matchingVariant.id, 'Price:', matchingVariant.price);
            
            // Store current variant ID
            FrameLensThumbnails.currentVariantId = matchingVariant.id;
            
            this.updatePrice(matchingVariant);
            
            // Update variant select for cart
            const variantSelect = document.querySelector('select[name="id"]');
            if (variantSelect) {
              variantSelect.value = matchingVariant.id;
              variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
              // console.log('✓ Cart will use variant:', matchingVariant.id);
              
              // Force update hidden input if exists
              const hiddenInput = document.querySelector('input[name="id"][type="hidden"]');
              if (hiddenInput) {
                hiddenInput.value = matchingVariant.id;
                // console.log('✓ Hidden input updated');
              }
            }
          } else {
            // console.log('✗ No variant found for:', variantName, '+ Size:', currentSize);
          }
        }
      }
    },

    updateURL: function(variantId) {
      if (window.history && window.history.pushState) {
        const newUrl = window.location.pathname + `?variant=${variantId}`;
        window.history.pushState({path: newUrl}, '', newUrl);
        // console.log('✓ URL updated:', newUrl);
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => FrameLensThumbnails.init());
  } else {
    FrameLensThumbnails.init();
  }
  
  // Make available globally for integration with zoom script
  window.FrameLensThumbnails = FrameLensThumbnails;
  
  // Also try after delays
  setTimeout(() => FrameLensThumbnails.init(), 1000);
  setTimeout(() => FrameLensThumbnails.init(), 2000);
  
  window.frameLensThumbnails = FrameLensThumbnails;
  
  // Add CSS styles for better arrow appearance and gallery
  const style = document.createElement('style');
  style.textContent = `
    /* Hide all existing Shopify gallery arrows - BUT NOT our custom ones */
    .slideshow__btn:not(.variant-arrow-nav),
    .product__media-toggle:not(.variant-arrow-nav),
    [data-slideshow-btn]:not(.variant-arrow-nav),
    .slider-button:not(.variant-arrow-nav),
    .gallery-arrow:not(.variant-arrow-nav),
    .product-gallery__arrow:not(.variant-arrow-nav),
    .slideshow-controls__arrow:not(.variant-arrow-nav),
    .slideshow__controls button:not(.variant-arrow-nav),
    .product__media-list button:not(.variant-arrow-nav),
    .product-thumbnails button:not(.variant-arrow-nav),
    .flickity-prev-next-button:not(.variant-arrow-nav),
    .slick-arrow:not(.variant-arrow-nav),
    .swiper-button-prev:not(.variant-arrow-nav),
    .swiper-button-next:not(.variant-arrow-nav) {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    
    /* Ensure our custom arrows are always visible */
    .variant-arrow-nav {
      display: flex !important;
      visibility: visible !important;
      opacity: 1 !important;
      pointer-events: auto !important;
      transition: all 0.3s ease !important;
      background: transparent !important;
      color: #000 !important;
    }
    
    .variant-arrow-nav:hover {
      color: #666 !important;
    }
    
    .variant-arrow-nav:active {
      transform: translateY(-50%) scale(0.95) !important;
    }
    
    .variant-gallery-thumbnails {
      scrollbar-width: thin;
      scrollbar-color: #ccc #f0f0f0;
    }
    
    .variant-gallery-thumbnails::-webkit-scrollbar {
      height: 6px;
    }
    
    .variant-gallery-thumbnails::-webkit-scrollbar-track {
      background: #f0f0f0;
      border-radius: 3px;
    }
    
    .variant-gallery-thumbnails::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 3px;
    }
    
    .variant-gallery-thumbnails::-webkit-scrollbar-thumb:hover {
      background: #999;
    }
    
    .gallery-thumb {
      transition: all 0.2s ease !important;
    }
    
    .gallery-thumb:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    }
  `;
  document.head.appendChild(style);
  
  // console.log('=== Frame-Lens Thumbnail Script Loaded ===');
})();
/**
 * ========================================
 * VARIANT-AWARE IMAGE ZOOM SYSTEM
 * ========================================
 * Custom zoom dialog that shows only variant-specific images
 */

(function() {
  'use strict';

  const VariantImageZoom = {
    
    initialized: false,
    isZoomOpen: false,
    currentZoomImages: [],
    currentZoomIndex: 0,
    
    init: function() {
      // Prevent multiple initializations
      if (this.initialized) {
      console.log('⚠️ VariantImageZoom already initialized');
        return;
      }
      
      
      // Check if we're on a product page
      const isProduct = this.isProductPage();
      
      if (!isProduct) {
        console.log('⚠️ Not a product page - zoom disabled');
        this.disableZoomCompletely();
        return;
      }
      
      console.log('✅ Product page detected - enabling zoom');
      this.enableZoomForProductPage();
      
      // Mark as initialized
      this.initialized = true;
    },
    
    // Completely disable zoom functionality
    disableZoomCompletely: function() {
      console.log('🚫 DISABLING ZOOM COMPLETELY');
      
      // Remove any existing zoom elements
      const overlay = document.getElementById('ZOOM_OVERLAY');
      if (overlay) overlay.remove();
      
      // Remove all image click handlers
      const images = document.querySelectorAll('img[data-zoom-handler-added="true"]');
      images.forEach(img => {
        img.removeAttribute('data-zoom-handler-added');
        img.style.cursor = '';
        img.onclick = null;
        
        // Remove all event listeners by cloning
        const newImg = img.cloneNode(true);
        img.parentNode.replaceChild(newImg, img);
      });
      
      // Disable all zoom functions
      this.isZoomOpen = false;
      this.currentZoomImages = [];
      this.currentZoomIndex = 0;
      
      console.log('✅ Zoom functionality completely disabled');
    },
    
    // Enable zoom only for product pages
    enableZoomForProductPage: function() {
      console.log('✅ ENABLING ZOOM FOR PRODUCT PAGE');
      
      this.hideDefaultZoomDialogs();
      this.setupImageClickHandlers();
      this.setupKeyboardNavigation();
      
      console.log('✅ VariantImageZoom enabled for product page');
    },
    
    // BALANCED: Check if current page is a product page (not too strict)
    isProductPage: function() {
      console.log('🔍 BALANCED Product Page Detection Starting...');
      
      const path = window.location.pathname;
      const fullUrl = window.location.href;
      
      console.log('Current URL:', fullUrl);
      console.log('Current path:', path);
      
      // FIRST: Exclude obvious non-product pages
      if (path.includes('/collections/') || 
          path.includes('/collection/') ||
          path === '/' ||
          path === '' ||
          path.includes('/pages/') ||
          path.includes('/blogs/') ||
          path.includes('/cart') ||
          path.includes('/account')) {
        console.log('❌ URL is clearly not a product page');
        return false;
      }
      
      // SECOND: Check body classes for collection indicators (STRICT)
      const bodyClasses = document.body.className.toLowerCase();
      console.log('Body classes:', bodyClasses);
      
      if (bodyClasses.includes('template-collection') || 
          bodyClasses.includes('collection-template') ||
          bodyClasses.includes('page-collection') ||
          bodyClasses.includes('list-collections')) {
        console.log('❌ Body class indicates collection page');
        return false;
      }
      
      // THIRD: Check for collection-specific elements (STRICT)
      const collectionElements = [
        '.collection-grid',
        '.collection-list', 
        '.product-grid',
        '.collection-products',
        '.collection-header'
      ];
      
      for (let selector of collectionElements) {
        if (document.querySelector(selector)) {
          console.log('❌ Found collection element:', selector);
          return false;
        }
      }
      
      // FOURTH: Positive checks for product page (FLEXIBLE)
      let productScore = 0;
      
      // URL check (flexible)
      if (path.includes('/products/')) {
        productScore += 3;
        console.log('✅ URL contains /products/ (+3 points)');
      }
      
      // Body class check (flexible)
      if (bodyClasses.includes('template-product') || 
          bodyClasses.includes('product-template') ||
          bodyClasses.includes('page-product')) {
        productScore += 3;
        console.log('✅ Product body class found (+3 points)');
      }
      
      // Product elements check (flexible)
      const productElements = [
        'form[action*="/cart/add"]',
        '.product-form',
        '[data-product-form]',
        '.product__info',
        '.product__media',
        '.product-media',
        '.variant-picker'
      ];
      
      let foundProductElements = 0;
      productElements.forEach(selector => {
        if (document.querySelector(selector)) {
          foundProductElements++;
          console.log('✅ Found product element:', selector);
        }
      });
      
      if (foundProductElements >= 2) {
        productScore += 2;
        console.log(`✅ Found ${foundProductElements} product elements (+2 points)`);
      }
      
      // Shopify product data check (flexible)
      if ((typeof ShopifyAnalytics !== 'undefined' && 
           ShopifyAnalytics.meta && 
           ShopifyAnalytics.meta.product) ||
          (typeof window.product !== 'undefined' && window.product)) {
        productScore += 2;
        console.log('✅ Shopify product data found (+2 points)');
      }
      
      console.log(`📊 Total product score: ${productScore}/10`);
      
      // DECISION: Need at least 5 points to be considered a product page
      if (productScore >= 5) {
        console.log('✅ BALANCED CHECK PASSED - This IS a product page');
        return true;
      } else {
        console.log('❌ BALANCED CHECK FAILED - This is NOT a product page');
        return false;
      }
    },
    
    // Simple test function to check if everything works
    testZoom: function() {
      console.log('🧪 Testing zoom functionality...');
      
      if (!this.isProductPage()) {
        console.log('❌ Cannot test zoom - not on a product page');
        return false;
      }
      
      this.currentZoomImages = [
        'https://via.placeholder.com/800x600/FF0000/FFFFFF?text=RED+IMAGE',
        'https://via.placeholder.com/800x600/00FF00/FFFFFF?text=GREEN+IMAGE',
        'https://via.placeholder.com/800x600/0000FF/FFFFFF?text=BLUE+IMAGE'
      ];
      this.currentZoomIndex = 0;
      this.openZoom();
      return true;
    },
    
    hideDefaultZoomDialogs: function() {
      console.log('🚫 Hiding ONLY zoom dialogs (preserving mini cart)...');
      
      // SPECIFIC: Only hide zoom-related dialogs, NOT mini cart or other modals
      const hideZoomElements = () => {
        const zoomSelectors = [
          'zoom-dialog',
          'zoom-dialog dialog',
          'dialog[id*="zoom"]:not([id*="cart"]):not([id*="drawer"])',
          'dialog[ref="dialog"][aria-label*="zoom"]',
          '.dialog-zoomed-gallery',
          '.dialog-thumbnails-list',
          '[data-modal-opener*="zoom"]'
        ];
        
        zoomSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            // Double-check this is NOT a cart/drawer element
            const isCartElement = el.id && (
              el.id.includes('cart') || 
              el.id.includes('drawer') || 
              el.id.includes('minicart') ||
              el.classList.contains('cart') ||
              el.classList.contains('drawer') ||
              el.classList.contains('mini-cart')
            );
            
            if (!isCartElement) {
              el.style.display = 'none';
              el.style.visibility = 'hidden';
              el.style.zIndex = '-1';
              
              if (el.hasAttribute('open')) {
                el.removeAttribute('open');
              }
              
              console.log('✅ Hidden zoom dialog:', el.tagName, el.id || el.className);
            } else {
              console.log('🛡️ Preserved cart element:', el.tagName, el.id || el.className);
            }
          });
        });
      };
      
      // Hide zoom elements immediately
      hideZoomElements();
      
      // Watch for new zoom dialogs (but preserve cart functionality)
      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1) { // Element node
                // Only process if it's a zoom-related element
                if (node.matches && (
                  node.matches('zoom-dialog') ||
                  node.matches('dialog[id*="zoom"]') ||
                  node.matches('.dialog-zoomed-gallery')
                )) {
                  hideZoomElements();
                }
              }
            });
          }
        });
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      console.log('✅ Zoom dialogs hidden, mini cart preserved');
    },
    
    setupImageClickHandlers: function() {
      // CRITICAL: Double-check we're on a product page before adding handlers
      if (!this.isProductPage()) {
        console.log('⚠️ SECURITY: Not on product page - skipping image click handlers');
        return;
      }
      
      console.log('🖼️ Setting up image click handlers for PRODUCT PAGE ONLY...');
      
      // Add click handlers to main product images
      const addClickHandlers = () => {
        // STRICT: Only add handlers if still on product page
        if (!this.isProductPage()) {
          console.log('⚠️ Page changed - removing image handlers');
          this.disableZoomCompletely();
          return;
        }
        
        // Try multiple selectors to find product images
        const imageSelectors = [
          '.product__media img',
          '.product-media img', 
          '[data-product-media] img',
          '.product__main-photos img',
          '.product-single__photo img',
          'slideshow-container img',
          '.slideshow img',
          '.product-gallery img',
          '.main-product-image img',
          '.product-image img'
        ];
        
        let foundImages = 0;
        
        imageSelectors.forEach(selector => {
          const images = document.querySelectorAll(selector);
          images.forEach(img => {
            // Skip if already has our handler
            if (img.dataset.zoomHandlerAdded) return;
            
            // STRICT: Skip if not a product image (avoid collection/other page images)
            if (!this.isProductImage(img)) return;
            
            // CRITICAL: Final check - ensure we're still on product page
            if (!this.isProductPage()) {
              console.log('⚠️ Not on product page - skipping image handler');
              return;
            }
            
            // Mark as handled
            img.dataset.zoomHandlerAdded = 'true';
            
            // Remove any existing handlers
            img.removeEventListener('click', this.handleImageClick);
            
            // Add our custom handler with multiple approaches
            const self = this;
            
            // Method 1: addEventListener with capture
            img.addEventListener('click', function(e) {
              // SECURITY CHECK: Verify we're still on product page
              if (!self.isProductPage()) {
                console.log('� SECURITY: Not on product page - blocking zoom');
                e.preventDefault();
                e.stopPropagation();
                return false;
              }
              
              console.log('🖱️ Product image clicked via addEventListener!');
              self.handleImageClick.call(self, e);
            }, true);
            
            // Method 2: onclick property (backup)
            img.onclick = function(e) {
              // SECURITY CHECK: Verify we're still on product page
              if (!self.isProductPage()) {
                console.log('🚫 SECURITY: Not on product page - blocking zoom');
                e.preventDefault();
                e.stopPropagation();
                return false;
              }
              
              console.log('🖱️ Product image clicked via onclick!');
              self.handleImageClick.call(self, e);
              return false;
            };
            
            // Method 3: mousedown (extra backup)
            img.addEventListener('mousedown', function(e) {
              if (e.button === 0) { // Left click only
                // SECURITY CHECK: Verify we're still on product page
                if (!self.isProductPage()) {
                  console.log('🚫 SECURITY: Not on product page - blocking zoom');
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }
                
                console.log('🖱️ Product image mousedown!');
                setTimeout(() => {
                  self.handleImageClick.call(self, e);
                }, 10);
              }
            }, true);
            
            // Style the image to show it's clickable
            img.style.cursor = 'zoom-in';
            img.style.pointerEvents = 'auto';
            img.style.userSelect = 'none';
            
            // Add visual feedback
            img.addEventListener('mouseenter', function() {
              if (self.isProductPage()) {
                this.style.opacity = '0.8';
                this.style.transition = 'opacity 0.2s';
              }
            });
            
            img.addEventListener('mouseleave', function() {
              this.style.opacity = '1';
            });
            
            foundImages++;
            console.log(`✅ Click handler added to product image ${foundImages}: ${selector}`);
          });
        });
        
        console.log(`🎯 Total product images with click handlers: ${foundImages}`);
        
        if (foundImages === 0) {
          console.warn('⚠️ No product images found for zoom functionality');
        }
      };
      
      // Setup handlers immediately and with delays
      addClickHandlers.call(this);
      setTimeout(() => addClickHandlers.call(this), 500);
      setTimeout(() => addClickHandlers.call(this), 1000);
      setTimeout(() => addClickHandlers.call(this), 2000);
      
      // Re-setup handlers when DOM changes (but only if still on product page)
      const observer = new MutationObserver(() => {
        if (this.isProductPage()) {
          setTimeout(() => addClickHandlers.call(this), 100);
        } else {
          console.log('⚠️ Page changed - disabling zoom');
          this.disableZoomCompletely();
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      console.log('🎯 Product image click handlers setup complete');
    },
    
    // Check if an image is a product image (not collection/other page image)
    isProductImage: function(img) {
      // Check if image is in product-specific containers
      const productContainers = [
        '.product__media',
        '.product-media',
        '[data-product-media]',
        '.product__main-photos',
        '.product-single__photo',
        '.product-gallery',
        '.main-product-image',
        '.product-image',
        'slideshow-container'
      ];
      
      // Check if image or its parents have product-specific classes
      let element = img;
      while (element && element !== document.body) {
        for (let container of productContainers) {
          if (element.matches && element.matches(container)) {
            return true;
          }
        }
        element = element.parentElement;
      }
      
      // Check image size (product images are usually larger)
      if (img.offsetWidth >= 200 && img.offsetHeight >= 200) {
        return true;
      }
      
      // Check if image src contains product-like patterns
      const src = img.src.toLowerCase();
      if (src.includes('product') || src.includes('variant') || src.includes('shopify')) {
        return true;
      }
      
      return false;
    },
    
    handleImageClick: function(e) {
      // CRITICAL SECURITY CHECK: Must be on product page
      if (!this.isProductPage()) {
        console.log('🚫 SECURITY BLOCK: Not on product page - zoom blocked');
        e.preventDefault();
        return false;
      }
      
      
      // GENTLE: Only prevent default image behavior, don't block other events
      e.preventDefault();
      // REMOVED: e.stopPropagation() and e.stopImmediatePropagation() to allow mini cart to work
      
      // CRITICAL: Temporarily disable ALL modal/zoom elements on the page
      const disableAllModals = () => {
        const modalElements = document.querySelectorAll(
          'zoom-dialog, modal-dialog, dialog[id*="zoom"], .modal[id*="zoom"], .lightbox, [data-modal-opener*="zoom"], .product__modal-opener'
        );
        
        modalElements.forEach(el => {
          // Only disable zoom-related modals, NOT cart/drawer elements
          const isCartElement = el.id && (
            el.id.includes('cart') || 
            el.id.includes('drawer') || 
            el.id.includes('minicart') ||
            el.classList.contains('cart') ||
            el.classList.contains('drawer') ||
            el.classList.contains('mini-cart')
          );
          
          if (!isCartElement) {
            el.style.display = 'none !important';
            el.style.pointerEvents = 'none !important';
            el.disabled = true;
            if (el.hasAttribute('open')) el.removeAttribute('open');
          }
        });
      };
      
      disableAllModals();
      
      console.log('� Clicked image src:', e.target.src);
      
      // CRITICAL: Reset zoom state
      this.currentZoomImages = [];
      this.currentZoomIndex = 0;
      
      // Method 1: Try to get variant-specific images from FrameLensThumbnails (BEST)
      if (window.FrameLensThumbnails && window.FrameLensThumbnails.currentVariantImages && 
          window.FrameLensThumbnails.currentVariantImages.length > 0) {
        
        console.log('✅ Using variant images from FrameLensThumbnails');
        console.log('📊 Original variant images:', window.FrameLensThumbnails.currentVariantImages.length);
        
        // AGGRESSIVE duplicate removal - filter out empty/invalid images first
        const validImages = window.FrameLensThumbnails.currentVariantImages.filter(src => {
          return src && 
                 typeof src === 'string' && 
                 src.trim() !== '' && 
                 src !== 'undefined' && 
                 src !== 'null' &&
                 src.startsWith('http');
        });
        
        // Remove duplicates using Set
        this.currentZoomImages = [...new Set(validImages)];
        
        console.log('📊 After filtering & deduplication:', this.currentZoomImages.length);
        console.log('�️ Unique variant images:', this.currentZoomImages.map(src => src.substring(src.lastIndexOf('/') + 1)));
        
        // Find clicked image index
        this.currentZoomIndex = this.currentZoomImages.indexOf(e.target.src);
        if (this.currentZoomIndex === -1) {
          // If clicked image not in variant list, add it as first
          this.currentZoomImages.unshift(e.target.src);
          this.currentZoomIndex = 0;
          console.log('📍 Added clicked image as first image');
        }
      }
      // Method 2: Try variant gallery thumbnails
      else {
        console.log('⚠️ No FrameLensThumbnails data, trying variant gallery...');
        
        const uniqueImages = new Set();
        
        // Look for variant-specific gallery thumbnails
        const variantGallery = document.querySelector('.variant-gallery-thumbnails');
        if (variantGallery) {
          const galleryImages = variantGallery.querySelectorAll('img');
          galleryImages.forEach(img => {
            if (img.src && img.src.trim() !== '' && img.src.startsWith('http')) {
              uniqueImages.add(img.src);
            }
          });
          console.log('✅ Found variant gallery images:', uniqueImages.size);
        }
        
        // Method 3: Fallback to all product images
        if (uniqueImages.size === 0) {
          console.log('⚠️ No variant gallery, using all product images...');
          const allProductImages = this.getAllProductImages();
          allProductImages.forEach(src => {
            if (src && src.trim() !== '' && src.startsWith('http')) {
              uniqueImages.add(src);
            }
          });
        }
        
        this.currentZoomImages = Array.from(uniqueImages);
        
        // Find clicked image index
        this.currentZoomIndex = this.currentZoomImages.indexOf(e.target.src);
        if (this.currentZoomIndex === -1) {
          this.currentZoomImages.unshift(e.target.src);
          this.currentZoomIndex = 0;
        }
      }
      
      // FINAL VALIDATION: Ensure we have valid images
      if (this.currentZoomImages.length === 0) {
        console.log('⚠️ No images found, using clicked image only');
        this.currentZoomImages = [e.target.src];
        this.currentZoomIndex = 0;
      }
      
      console.log('🎯 FINAL ZOOM SETUP:');
      console.log('📊 Total images:', this.currentZoomImages.length);
      console.log('📍 Starting index:', this.currentZoomIndex);
      console.log('🖼️ Images:', this.currentZoomImages.map(src => src.substring(src.lastIndexOf('/') + 1)));
      
      // CRITICAL: Final security check before opening zoom
      if (!this.isProductPage()) {
        console.log('🚫 FINAL SECURITY CHECK FAILED - Not on product page');
        return false;
      }
      
      // CRITICAL: Prevent any page navigation after opening zoom
      setTimeout(() => {
        // Disable all navigation for a short period
        const preventNavigation = (navEvent) => {
          navEvent.preventDefault();
          navEvent.stopPropagation();
          navEvent.stopImmediatePropagation();
          return false;
        };
        
        // Add temporary navigation blockers
        document.addEventListener('click', preventNavigation, true);
        document.addEventListener('mousedown', preventNavigation, true);
        
        // Remove blockers after zoom opens
        setTimeout(() => {
          document.removeEventListener('click', preventNavigation, true);
          document.removeEventListener('mousedown', preventNavigation, true);
        }, 1000);
        
      }, 10);
      
      // Open zoom with variant-specific images
      this.openZoom();
      
      // Return false to prevent any default behavior
      return false;
    },
    
    getAllProductImages: function() {
      console.log('🔍 Getting all product images...');
      
      const uniqueImages = new Set();
      
      // Try multiple selectors to find product images
      const imageSelectors = [
        '.product__media img',
        '.product-media img', 
        '[data-product-media] img',
        '.product__main-photos img',
        '.product-single__photo img',
        'slideshow-container img',
        '.slideshow img',
        '.product-gallery img'
      ];
      
      imageSelectors.forEach(selector => {
        const images = document.querySelectorAll(selector);
        images.forEach(img => {
          if (img.src && img.src.trim() !== '' && img.offsetWidth > 50 && img.offsetHeight > 50) {
            uniqueImages.add(img.src);
          }
        });
      });
      
      const result = Array.from(uniqueImages);
      console.log('📊 Found unique product images:', result.length);
      
      return result;
    },
    
    openZoom: function() {
      if (this.isZoomOpen || this.currentZoomImages.length === 0) return;
      
      console.log('🔍 Opening custom zoom...');
      this.isZoomOpen = true;
      
      // CRITICAL: Add body class to prevent any default modals
      document.body.classList.add('zoom-open');
      document.body.style.overflow = 'hidden';
      
      // Remove any existing zoom first
      const existingOverlay = document.getElementById('ZOOM_OVERLAY');
      if (existingOverlay) {
        existingOverlay.remove();
      }
      
      // AGGRESSIVE: Disable all default zoom elements before opening ours
      const disableAllDefaults = () => {
        const defaultElements = document.querySelectorAll(
          'zoom-dialog, modal-dialog, dialog, .modal, .lightbox, [data-modal-opener], .product__modal-opener'
        );
        defaultElements.forEach(el => {
          el.style.display = 'none !important';
          el.style.pointerEvents = 'none !important';
          el.disabled = true;
          if (el.hasAttribute('open')) el.removeAttribute('open');
        });
      };
      
      disableAllDefaults();
      
      // Create simple Shopify-style zoom
      const zoomHTML = `
        <div id="ZOOM_OVERLAY" style="
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          background: rgba(0, 0, 0, 0.9) !important;
          z-index: 999999999 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        ">
          
          <img id="ZOOM_IMAGE" src="${this.currentZoomImages[this.currentZoomIndex]}" style="
            max-width: 75vw !important;
            max-height: 75vh !important;
            object-fit: contain !important;
            border-radius: 4px !important;
          ">
          
          <!-- Simple Close Button -->
          <button id="ZOOM_CLOSE" style="
            position: fixed !important;
            top: 20px !important;
            right: 20px !important;
            width: 44px !important;
            height: 44px !important;
            background: rgba(255, 255, 255, 0.9) !important;
            color: #000 !important;
            border: none !important;
            border-radius: 4px !important;
            font-size: 18px !important;
            cursor: pointer !important;
            z-index: 9999999999 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
          ">✕</button>
          
          ${this.currentZoomImages.length > 1 ? `
            <!-- Previous Button -->
            <button id="ZOOM_PREV" style="
              position: fixed !important;
              left: 20px !important;
              top: 50% !important;
              transform: translateY(-50%) !important;
              width: 44px !important;
              height: 44px !important;
              background: rgba(255, 255, 255, 0.9) !important;
              color: #000 !important;
              border: none !important;
              border-radius: 4px !important;
              font-size: 16px !important;
              cursor: pointer !important;
              z-index: 9999999999 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
              box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
            ">‹</button>
            
            <!-- Next Button -->
            <button id="ZOOM_NEXT" style="
              position: fixed !important;
              right: 20px !important;
              top: 50% !important;
              transform: translateY(-50%) !important;
              width: 44px !important;
              height: 44px !important;
              background: rgba(255, 255, 255, 0.9) !important;
              color: #000 !important;
              border: none !important;
              border-radius: 4px !important;
              font-size: 16px !important;
              cursor: pointer !important;
              z-index: 9999999999 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
              box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
            ">›</button>
            
            <!-- Image Counter -->
            <div id="ZOOM_COUNTER" style="
              position: fixed !important;
              bottom: 20px !important;
              left: 50% !important;
              transform: translateX(-50%) !important;
              background: rgba(0, 0, 0, 0.7) !important;
              color: white !important;
              padding: 8px 16px !important;
              border-radius: 20px !important;
              font-size: 14px !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
              z-index: 9999999999 !important;
            ">${this.currentZoomIndex + 1} / ${this.currentZoomImages.length}</div>
          ` : ''}
          
        </div>
      `;
      
      // Add to page
      document.body.insertAdjacentHTML('beforeend', zoomHTML);
      
      // Add event listeners with multiple approaches for reliability
      const self = this;
      
      // Close button - multiple event handlers for maximum reliability
      const closeBtn = document.getElementById('ZOOM_CLOSE');
      if (closeBtn) {
        // Method 1: onclick property
        closeBtn.onclick = function(e) {
          e.preventDefault();
          e.stopPropagation();
          console.log('� Close clicked via onclick!');
          self.closeZoom();
          return false;
        };
        
        // Method 2: addEventListener
        closeBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          console.log('� Close clicked via addEventListener!');
          self.closeZoom();
        }, true);
        
        // Method 3: mousedown backup
        closeBtn.addEventListener('mousedown', function(e) {
          if (e.button === 0) { // Left click only
            e.preventDefault();
            e.stopPropagation();
            console.log('🔴 Close mousedown!');
            setTimeout(() => self.closeZoom(), 10);
          }
        }, true);
      }
      
      // Navigation buttons - only if multiple images
      if (this.currentZoomImages.length > 1) {
        const prevBtn = document.getElementById('ZOOM_PREV');
        const nextBtn = document.getElementById('ZOOM_NEXT');
        
        if (prevBtn) {
          // Previous button handlers
          prevBtn.onclick = function(e) {
            e.preventDefault();
            e.stopPropagation();
            console.log('🔵 Prev clicked via onclick!');
            self.navigateZoom('prev');
            return false;
          };
          
          prevBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            console.log('🔵 Prev clicked via addEventListener!');
            self.navigateZoom('prev');
          }, true);
          
          prevBtn.addEventListener('mousedown', function(e) {
            if (e.button === 0) {
              e.preventDefault();
              e.stopPropagation();
              console.log('🔵 Prev mousedown!');
              setTimeout(() => self.navigateZoom('prev'), 10);
            }
          }, true);
        }
        
        if (nextBtn) {
          // Next button handlers
          nextBtn.onclick = function(e) {
            e.preventDefault();
            e.stopPropagation();
            console.log('🟢 Next clicked via onclick!');
            self.navigateZoom('next');
            return false;
          };
          
          nextBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            console.log('🟢 Next clicked via addEventListener!');
            self.navigateZoom('next');
          }, true);
          
          nextBtn.addEventListener('mousedown', function(e) {
            if (e.button === 0) {
              e.preventDefault();
              e.stopPropagation();
              console.log('🟢 Next mousedown!');
              setTimeout(() => self.navigateZoom('next'), 10);
            }
          }, true);
        }
      }
      
      // Click outside to close - multiple approaches
      const overlay = document.getElementById('ZOOM_OVERLAY');
      if (overlay) {
        overlay.onclick = function(e) {
          if (e.target.id === 'ZOOM_OVERLAY') {
            console.log('🖱️ Overlay clicked - closing via onclick');
            self.closeZoom();
          }
        };
        
        overlay.addEventListener('click', function(e) {
          if (e.target.id === 'ZOOM_OVERLAY') {
            console.log('🖱️ Overlay clicked - closing via addEventListener');
            self.closeZoom();
          }
        }, true);
        
        overlay.addEventListener('mousedown', function(e) {
          if (e.target.id === 'ZOOM_OVERLAY' && e.button === 0) {
            console.log('🖱️ Overlay mousedown - closing');
            setTimeout(() => self.closeZoom(), 10);
          }
        }, true);
      }
      
      // Add keyboard support
      this.setupKeyboard();
      
      console.log('✅ Zoom opened with', this.currentZoomImages.length, 'images');
    },
    
    setupKeyboard: function() {
      const self = this;
      
      // Remove existing handler
      if (this.keyboardHandler) {
        document.removeEventListener('keydown', this.keyboardHandler);
      }
      
      this.keyboardHandler = function(e) {
        if (!self.isZoomOpen) return;
        
        if (e.key === 'Escape') {
          console.log('⌨️ Escape pressed');
          self.closeZoom();
        } else if (e.key === 'ArrowLeft') {
          console.log('⌨️ Left arrow pressed');
          self.navigateZoom('prev');
        } else if (e.key === 'ArrowRight') {
          console.log('⌨️ Right arrow pressed');
          self.navigateZoom('next');
        }
      };
      
      document.addEventListener('keydown', this.keyboardHandler);
    },
    
    navigateZoom: function(direction) {
      if (this.currentZoomImages.length <= 1) return;
      
      console.log('🔄 Navigating:', direction);
      
      // Update index
      if (direction === 'next') {
        this.currentZoomIndex = (this.currentZoomIndex + 1) % this.currentZoomImages.length;
      } else {
        this.currentZoomIndex = (this.currentZoomIndex - 1 + this.currentZoomImages.length) % this.currentZoomImages.length;
      }
      
      console.log('📸 New index:', this.currentZoomIndex);
      
      // Update image with smooth transition
      const img = document.getElementById('ZOOM_IMAGE');
      if (img) {
        img.style.opacity = '0.5';
        setTimeout(() => {
          img.src = this.currentZoomImages[this.currentZoomIndex];
          img.style.opacity = '1';
        }, 100);
      }
      
      // Update counter
      const counter = document.getElementById('ZOOM_COUNTER');
      if (counter) {
        counter.textContent = `${this.currentZoomIndex + 1} / ${this.currentZoomImages.length}`;
      }
      
      console.log('✅ Navigation completed');
    },
    
    closeZoom: function() {
      console.log('🔴 Closing custom zoom...');
      
      // Remove zoom overlay
      const overlay = document.getElementById('ZOOM_OVERLAY');
      if (overlay) {
        overlay.remove();
        console.log('✅ Zoom overlay removed');
      }
      
      // Remove keyboard handler
      if (this.keyboardHandler) {
        document.removeEventListener('keydown', this.keyboardHandler);
        this.keyboardHandler = null;
      }
      
      // CRITICAL: Remove body class and restore scroll
      document.body.classList.remove('zoom-open');
      document.body.style.overflow = '';
      
      // Reset zoom state
      this.isZoomOpen = false;
      
      // AGGRESSIVE: Re-enable any disabled elements (but keep default zoom disabled)
      setTimeout(() => {
        // Only re-enable non-zoom elements
        const disabledElements = document.querySelectorAll('[disabled]:not(zoom-dialog):not(modal-dialog):not([data-modal-opener])');
        disabledElements.forEach(el => {
          if (!el.matches('zoom-dialog, modal-dialog, [data-modal-opener], .modal, .lightbox')) {
            el.disabled = false;
          }
        });
      }, 100);
      
      console.log('✅ Custom zoom closed successfully');
      console.log('🚫 Default zoom dialogs remain disabled');
    },
    
    // Debug function to help troubleshoot issues
    debugZoom: function() {
      console.log('🔧 ZOOM DEBUG INFORMATION');
      console.log('=========================');
      
      console.log('VariantImageZoom loaded:', !!window.VariantImageZoom);
      console.log('Is product page:', this.isProductPage());
      console.log('Zoom is open:', this.isZoomOpen);
      console.log('Current zoom images:', this.currentZoomImages.length);
      console.log('Current zoom index:', this.currentZoomIndex);
      
      if (this.currentZoomImages.length > 0) {
        console.log('Zoom images:', this.currentZoomImages.map(src => src.substring(src.lastIndexOf('/') + 1)));
      }
      
      // Check DOM elements
      const overlay = document.getElementById('ZOOM_OVERLAY');
      const image = document.getElementById('ZOOM_IMAGE');
      const closeBtn = document.getElementById('ZOOM_CLOSE');
      const prevBtn = document.getElementById('ZOOM_PREV');
      const nextBtn = document.getElementById('ZOOM_NEXT');
      
      console.log('\nDOM Elements:');
      console.log('Overlay:', !!overlay);
      console.log('Image:', !!image);
      console.log('Close button:', !!closeBtn);
      console.log('Prev button:', !!prevBtn);
      console.log('Next button:', !!nextBtn);
      
      if (closeBtn) {
        console.log('Close button onclick:', !!closeBtn.onclick);
        console.log('Close button visible:', closeBtn.offsetWidth > 0 && closeBtn.offsetHeight > 0);
      }
      
      // Check FrameLensThumbnails
      if (window.FrameLensThumbnails) {
        console.log('\nFrameLensThumbnails:');
        console.log('Available:', !!window.FrameLensThumbnails);
        console.log('Current variant images:', window.FrameLensThumbnails.currentVariantImages ? window.FrameLensThumbnails.currentVariantImages.length : 0);
      }
      
      // Check product images with handlers
      const imagesWithHandlers = document.querySelectorAll('img[data-zoom-handler-added="true"]');
      console.log('\nProduct images with zoom handlers:', imagesWithHandlers.length);
      
      return {
        loaded: !!window.VariantImageZoom,
        isProductPage: this.isProductPage(),
        isOpen: this.isZoomOpen,
        imageCount: this.currentZoomImages.length,
        currentIndex: this.currentZoomIndex,
        hasOverlay: !!overlay,
        hasCloseButton: !!closeBtn,
        handlerCount: imagesWithHandlers.length
      };
    },
    
    setupKeyboardNavigation: function() {
      document.addEventListener('keydown', (e) => {
        if (!this.isZoomOpen) return;
        
        switch(e.key) {
          case 'Escape':
            e.preventDefault();
            this.closeZoom();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            this.navigateZoom('prev');
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.navigateZoom('next');
            break;
        }
      });
    },
  };

  // Initialize ONLY on window load (not DOM ready)
  window.addEventListener('load', () => {
    console.log('🚀 Window loaded - initializing VariantImageZoom');
    setTimeout(() => {
      VariantImageZoom.init();
    }, 500);
  });
  
  // Fallback initialization with longer delays
  setTimeout(() => {
    if (!window.VariantImageZoom.initialized) {
      console.log('🔄 Fallback initialization');
      VariantImageZoom.init();
    }
  }, 3000);
  
  setTimeout(() => {
    if (!window.VariantImageZoom.initialized) {
      console.log('🔄 Final fallback initialization');
      VariantImageZoom.init();
    }
  }, 5000);
  
  // Make available globally
  window.VariantImageZoom = VariantImageZoom;
  
  // Add SPECIFIC CSS for zoom functionality (preserving mini cart)
  const zoomStyle = document.createElement('style');
  zoomStyle.textContent = `
    /* SPECIFIC: Hide only zoom dialogs, NOT cart/drawer elements */
    zoom-dialog,
    zoom-dialog dialog,
    dialog[id*="zoom"]:not([id*="cart"]):not([id*="drawer"]):not([id*="minicart"]),
    dialog[ref="dialog"][aria-label*="zoom"],
    .dialog-zoomed-gallery,
    .dialog-thumbnails-list {
      display: none !important;
      visibility: hidden !important;
      z-index: -1 !important;
    }
    
    /* PRESERVE: Cart, drawer, and mini cart functionality */
    dialog[id*="cart"],
    dialog[id*="drawer"], 
    dialog[id*="minicart"],
    .cart-drawer,
    .mini-cart,
    .cart-popup,
    .drawer,
    [data-drawer],
    cart-drawer,
    mini-cart {
      /* Don't hide these - they should work normally */
    }
    
    /* Ensure product images are clickable for zoom */
    .product__media img,
    .product-media img,
    [data-product-media] img {
      cursor: zoom-in;
    }
    
    /* Our custom zoom overlay styles */
    #ZOOM_OVERLAY {
      backdrop-filter: blur(2px) !important;
      -webkit-backdrop-filter: blur(2px) !important;
    }
    
    #ZOOM_IMAGE {
      transition: opacity 0.15s ease !important;
    }
    
    /* Button hover effects for our custom zoom */
    #ZOOM_CLOSE:hover,
    #ZOOM_PREV:hover,
    #ZOOM_NEXT:hover {
      background: rgba(255, 255, 255, 1) !important;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4) !important;
      transform: scale(1.05) !important;
    }
    
    #ZOOM_CLOSE:active,
    #ZOOM_PREV:active,
    #ZOOM_NEXT:active {
      transform: scale(0.95) !important;
    }
    
    /* IMPORTANT: Don't block cart functionality when zoom is open */
    body.zoom-open .cart-drawer,
    body.zoom-open .mini-cart,
    body.zoom-open cart-drawer,
    body.zoom-open mini-cart,
    body.zoom-open [data-drawer] {
      display: block !important;
      visibility: visible !important;
      pointer-events: auto !important;
      z-index: 999999999 !important;
    }
  `;
  document.head.appendChild(zoomStyle);

})();

/**
 * CRITICAL FIX: Mini Cart Protection CSS
 * Ensure mini cart functionality is preserved
 */
(function() {
  const miniCartProtectionCSS = document.createElement('style');
  miniCartProtectionCSS.id = 'mini-cart-protection-critical';
  miniCartProtectionCSS.textContent = `
    /* CRITICAL: Ensure mini cart elements are ALWAYS above zoom and functional */
    cart-drawer,
    mini-cart,
    .cart-drawer,
    .mini-cart,
    .cart-popup,
    .drawer,
    [data-drawer],
    dialog[id*="cart"],
    dialog[id*="drawer"],
    dialog[id*="minicart"],
    .cart-notification,
    .cart-toast {
      z-index: 99999999999 !important; /* Above everything including zoom */
      pointer-events: auto !important;
      display: block !important;
      visibility: visible !important;
    }
    
    /* Ensure cart drawer opens properly */
    cart-drawer[open],
    .cart-drawer[open],
    .cart-drawer.open,
    dialog[id*="cart"][open] {
      z-index: 99999999999 !important;
      display: block !important;
      visibility: visible !important;
      pointer-events: auto !important;
      opacity: 1 !important;
    }
    
    /* Ensure add to cart button is always clickable */
    button[name="add"],
    input[name="add"],
    .add-to-cart,
    .product-form__cart button,
    .product-form__buttons button {
      pointer-events: auto !important;
      z-index: 10000 !important;
    }
    
    /* Lower zoom overlay z-index to be below mini cart */
    #ZOOM_OVERLAY {
      z-index: 999999 !important; /* Below mini cart */
    }
    
    /* Zoom buttons below mini cart but above overlay */
    .zoom-btn,
    #ZOOM_CLOSE,
    #ZOOM_PREV,
    #ZOOM_NEXT,
    #ZOOM_COUNTER {
      z-index: 9999999 !important; /* Below mini cart but above overlay */
    }
  `;
  
  // Remove existing protection if any
  const existing = document.getElementById('mini-cart-protection-critical');
  if (existing) existing.remove();
  
  document.head.appendChild(miniCartProtectionCSS);
  
  console.log('🛡️ Mini cart protection CSS added');
})();